import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useSearchParams, Link } from "react-router-dom";
import useSWR from "swr";
import { DealStage } from "@equaliq/api-model-v2";
import type { DealVersion } from "@equaliq/api-model-v2";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { useOrg } from "@/contexts/OrgContext";
import { useDealOperations } from "@/hooks/deals/useDealOperations";
import { getDealApi } from "@/api/deal/GetDealApi";
import { listDealThreadsApi } from "@/api/deal/ListDealThreadsApi";
import { updateDealThreadApi } from "@/api/deal/UpdateDealThreadApi";
import { reprocessThreadApi, reconnectThreadApi, markThreadReadApi } from "@/api/integrations";
import {
  useDealIntermediateData,
  type IntermediateActivityEvent,
  type IntermediateFile,
} from "@/hooks/deals/useDealIntermediateData";
import { DocumentsTable } from "@/views/deals/components/DealPage/DocumentsTable";
import { useAriInboxThreads, useSelectedThreadMessages } from "@/hooks/inbox/useAriInboxThreads";
import {
  AlertTriangle,
  Archive,
  BriefcaseBusiness,
  Check,
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
  ExternalLink,
  FileEdit,
  FileText,
  Globe2,
  Inbox,
  Instagram,
  Link2,
  Loader2,
  Lock,
  Mail,
  MessageSquare,
  PanelLeft,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Paperclip,
  RefreshCw,
  Reply,
  Search,
  Send,
  SendHorizontal,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Users,
  X,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  Avatar,
  Badge,
  Button,
  Dialog,
  Input,
  ScrollArea,
  Sheet,
  Tabs,
  Textarea,
} from "@/components/ui";
import { AriBrandOrFallback } from "@/components/demo-ari/AriBrandIcon";
import { TextShimmer } from "@/components/primitives/text-shimmer";
import { AriIcon } from "@/components/sidebar/icons";
import { resolveAriBrandId } from "@/lib/ariBrands";
import {
  ARI_CLIENT_PROFILES,
  ARI_DEAL_STAGES,
  ARI_DRAFT_THREADS,
  ARI_INBOX_DEAL_TAG_FILTERS,
  ARI_INBOX_FOLDERS,
  ARI_INBOX_THREADS,
  ARI_LABELS,
  ARI_SMART_REPLIES,
  ARI_TEXT_THREADS,
  getAriCounterpartyByEmail,
  getAriThreadLinkedContract,
  type AriTextThread,
} from "@/mockData/ariInbox";
import { ARI_DEAL_LABEL_COLORS, ENGINE_DEAL_TYPE_COLORS } from "@/constants/deal.constants";
import type {
  AriClientProfile,
  AriCounterpartyProfile,
  AriDealStage,
  AriInboxAttachment,
  AriInboxDealTagFilter,
  AriInboxFolder,
  AriInboxMessage,
  AriInboxThread,
  AriInsightAlert,
  AriThreadLinkState,
} from "@/types/inbox.types";
import type { DealWithTitle } from "@/types/deal.types";
import type { AriDealLabelId } from "@/types/deal.types";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { PATH_ROOT } from "@/routes/paths";
import { cn } from "@/utils/Functions";
import DOMPurify from "dompurify";

type ViewMode = "conversations" | "notifications";
type ReplyMode = "reply" | "replyAll" | "forward";
type InsightMode = "client" | "sender";

interface LookupLink {
  label: string;
  href: string;
  icon?: ReactNode;
}

interface ComposeState {
  to: string;
  subject: string;
  body: string;
}

interface ChatMessage {
  role: "user" | "ari";
  text: string;
}

const EMPTY_COMPOSE: ComposeState = { to: "", subject: "", body: "" };

const folderIcons: Record<AriInboxFolder, ReactNode> = {
  inbox: <Inbox size={16} aria-hidden="true" />,
  messages: <MessageSquare size={16} aria-hidden="true" />,
  drafts: <FileEdit size={16} aria-hidden="true" />,
  sent: <Send size={16} aria-hidden="true" />,
  important: <Star size={16} aria-hidden="true" />,
};

const stageClasses: Record<AriDealStage, string> = {
  Prospecting: "border-border bg-muted/50 text-muted-foreground",
  "Under Review": "border-primary/20 bg-primary/8 text-primary",
  Negotiation: "border-primary/25 bg-primary/10 text-primary",
  "Pending Signature": "border-warning/30 bg-warning/10 text-warning-foreground",
  Signed: "border-success/30 bg-success/10 text-success-foreground",
  Closed: "border-border bg-muted/50 text-muted-foreground",
};

/** Projects the api-model DealStage enum onto the six-stage AriDealStage the
 *  inbox tracker renders. The DealRoomView pipeline strip is otherwise pinned
 *  to a hardcoded "Prospecting"; for a thread that carries a real dealId the
 *  deal's latest-version stage is the source of truth. Cancelled deals read as
 *  "Closed" (the tracker has no cancelled lane). Mirrors
 *  dealPipelineMappers.mapDealStageToPipelineStage, but targets the inbox's
 *  AriDealStage labels rather than the kanban PipelineStage. */
function mapDealStageToAriStage(stage: DealStage | undefined): AriDealStage {
  switch (stage) {
    case DealStage.drafting:
      return "Prospecting";
    case DealStage.negotiation:
      return "Negotiation";
    case DealStage.signing:
      return "Pending Signature";
    case DealStage.delivery:
      return "Signed";
    case DealStage.completed:
      return "Closed";
    case DealStage.cancelled:
      return "Closed";
    default:
      return "Prospecting";
  }
}

/** Picks the latest DealVersion (highest versionNumber) the same way DealPage
 *  derives the header stage, so the inbox Deal Room and the deal page agree on
 *  "current stage". Returns null when /deals/get returned no versions. */
function latestDealStage(apiDealData: unknown): DealStage | undefined {
  const versions = (apiDealData as { versions?: Record<string, DealVersion> } | undefined)?.versions;
  if (!versions) return undefined;
  const list = Object.values(versions);
  if (list.length === 0) return undefined;
  return [...list].sort((a, b) => b.versionNumber - a.versionNumber)[0]?.stage;
}

function fileSizeLabel(sizeBytes?: number | null): string {
  if (!sizeBytes) return "—";
  return sizeBytes >= 1_000_000
    ? `${(sizeBytes / 1_000_000).toFixed(1)} MB`
    : `${(sizeBytes / 1_000).toFixed(0)} KB`;
}

/** Projects the deal's real attachments (the same useDealIntermediateData.files
 *  list DealRoomView's DocumentsTable renders) into the AttachmentBar shape so
 *  a deal-linked thread's reading-pane attachment strip shows the actual
 *  ingested files instead of the hardcoded empty list. createdAt is the file's
 *  ingest date; tone stays neutral because per-file review status is engine
 *  work (Tier P) the frontend can't yet truthfully label. */
function dealFilesToAttachments(files: IntermediateFile[]): AriInboxAttachment[] {
  return files.map((f) => ({
    name: f.fileName,
    type: (f.fileType ?? "").includes("pdf") ? "pdf" : "file",
    size: fileSizeLabel(f.sizeBytes),
    date: f.createdAt
      ? new Date(f.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : "",
    status: "Attached",
    tone: "neutral",
  }));
}

const tagToneClasses = [
  "border-primary/20 bg-primary/8 text-primary",
  "border-success/25 bg-success/8 text-success-foreground",
  "border-warning/25 bg-warning/8 text-warning-foreground",
  "border-info/25 bg-info/8 text-info-foreground",
  "border-destructive/20 bg-destructive/8 text-destructive-foreground",
  "border-border bg-muted/70 text-muted-foreground",
];

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

const CLIENT_TAG_TONE = "border-transparent bg-muted text-muted-foreground";

function isDealTypeTag(id: string): id is AriDealLabelId {
  return (ARI_INBOX_DEAL_TAG_FILTERS as readonly string[]).includes(id);
}

function tagLabel(id: string) {
  return id
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function webSearchUrl(query: string) {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    listener();
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

function initialsFromName(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function threadMatchesDealTag(thread: AriInboxThread, tagFilter: AriInboxDealTagFilter | null) {
  if (!tagFilter) return true;
  return thread.dealTags.includes(tagFilter);
}

function threadMatchesSearch(thread: AriInboxThread, search: string) {
  if (!search.trim()) return true;
  const query = search.toLowerCase();
  return [
    thread.subject,
    thread.from.name,
    thread.from.email,
    thread.aiSummary,
    ...thread.keywords,
    ...thread.dealTags,
    ...thread.clientTags,
    ...thread.messages.flatMap((message) => [message.sender, message.body, message.preview]),
  ]
    .join(" ")
    .toLowerCase()
    .includes(query);
}

function textThreadMatchesSearch(thread: AriTextThread, search: string) {
  if (!search.trim()) return true;
  const query = search.toLowerCase();
  return [
    thread.title,
    thread.subtitle,
    thread.preview,
    ...thread.messages.flatMap((message) => [message.sender ?? "", message.body]),
  ]
    .join(" ")
    .toLowerCase()
    .includes(query);
}

function replaceThread(
  threads: AriInboxThread[],
  threadId: string,
  updater: (thread: AriInboxThread) => AriInboxThread
) {
  return threads.map((thread) => (thread.id === threadId ? updater(thread) : thread));
}

function lastItem<T>(items: T[]) {
  return items[items.length - 1];
}

/**
 * Tier C — a thread is "unopenable" when the backend liveness projection marked
 * its deal link orphaned (deal soft-deleted) or cross-org (deal in another org
 * the caller can't reach). These threads get the recovery panel instead of a
 * Deal Room. A thread with no linkState (demo mode, or an unlinked thread) is
 * never treated as orphaned — only an explicit non-"live" linkState triggers
 * recovery, so the affordance generalizes to any unopenable deal without
 * special-casing.
 */
function isThreadOrphanedOrCrossOrg(thread: AriInboxThread): boolean {
  return thread.linkState === "orphaned" || thread.linkState === "cross-org";
}

/**
 * R1-a — a thread the categorizer never linked to a deal (`linkState:
 * "unlinked"`). Only ever present when the "Needs review / All mail" toggle is
 * on; the deal-only default path drops these server-side. Like orphaned/cross
 * -org, an unlinked thread has no openable Deal Room — it gets the recovery
 * panel, whose primary action is R1-b force-process ("Process into deal").
 */
function isThreadUnlinked(thread: AriInboxThread): boolean {
  return thread.linkState === "unlinked";
}

/**
 * A thread that can't open a real Deal Room and instead gets the recovery
 * panel: orphaned (deleted deal), cross-org (deal in another workspace), or
 * unlinked (categorizer never linked it). Demo threads and live deal-linked
 * threads return false. Centralizing the three recovery states here keeps the
 * room-vs-panel routing one predicate instead of scattered linkState checks.
 */
function isThreadInRecovery(thread: AriInboxThread): boolean {
  return isThreadOrphanedOrCrossOrg(thread) || isThreadUnlinked(thread);
}

function AriDemoInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDemo = useIsDemo();
  // R1-a — "Needs review / All mail" view, DEFAULT OFF. When off, the inbox is
  // the deal-only lens (only categorizer-linked threads); the default path is
  // byte-for-byte unchanged. When on, the hook fetches the LEFT-joined list so
  // unlinked (categorizer-ignored) threads surface with their ignore reason +
  // a "Process into deal" action. Never on in demo mode (mock threads only).
  const [includeUnlinked, setIncludeUnlinked] = useState(false);
  // Demo-leak fix (default #4): on a /demo route the page renders mock threads
  // (ARI_INBOX_THREADS) and never reads nylasInbox.threads, so gate the real
  // Nylas connections fetch off entirely rather than firing it and discarding
  // the result. enabled:!isDemo threads through to useNylasConnections.
  const nylasInbox = useAriInboxThreads({ enabled: !isDemo, includeUnlinked: !isDemo && includeUnlinked });
  // Tier C — thread-link recovery. The selected org scopes the deal-picker
  // (/deals/list) and the delete mutation (which routes through the Deal
  // Manager via useDealOperations.deleteDeal). Gated off in demo mode: demo
  // threads never carry a linkState, so recovery never triggers there.
  const { selectedOrgId } = useOrg();
  const dealOps = useDealOperations(isDemo ? undefined : selectedOrgId);
  const isNarrow = useMediaQuery("(max-width: 1024px)");
  const [demoThreads, setDemoThreads] = useState(() => isDemo ? ARI_INBOX_THREADS : []);
  const [draftThreads, setDraftThreads] = useState(() => isDemo ? ARI_DRAFT_THREADS : []);
  const [sentThreads, setSentThreads] = useState<AriInboxThread[]>([]);
  const [textThreads, setTextThreads] = useState(() => isDemo ? ARI_TEXT_THREADS : []);
  const [activeFolder, setActiveFolder] = useState<AriInboxFolder>("inbox");
  const [activeDealTagFilter, setActiveDealTagFilter] = useState<AriInboxDealTagFilter | null>(
    null
  );
  const [viewMode, setViewMode] = useState<ViewMode>("conversations");
  const [search, setSearch] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState<string>(isDemo ? ARI_INBOX_THREADS[0].id : "");
  const [selectedTextThreadId, setSelectedTextThreadId] = useState<string>(isDemo ? ARI_TEXT_THREADS[0].id : "");

  // Locally-cleared unread state for the REAL route. Real threads come from the
  // read-only Nylas SWR cache, so the demo's setThreads(unread:false) optimistic
  // update is a no-op there — opening an email never cleared its red dot. This
  // set tracks threads the user has opened this session; we overlay unread:false
  // onto the live Nylas threads below so the dot clears immediately on open and
  // survives SWR refetches. (Persisting read-state back to Nylas is a separate
  // mark-as-read follow-up; this is the optimistic UI half.)
  const [readThreadIds, setReadThreadIds] = useState<Set<string>>(new Set());

  // In prod mode, threads come directly from the hook (no local state copy),
  // with the session-local read overlay applied. In demo mode, threads are
  // managed in local state for archive/delete/reply mutations.
  const threads = isDemo
    ? demoThreads
    : nylasInbox.threads.map((t) =>
        readThreadIds.has(t.id) && t.unread ? { ...t, unread: false } : t,
      );
  const setThreads = isDemo ? setDemoThreads : () => {};

  // Auto-select first thread in prod mode once loaded
  useEffect(() => {
    if (isDemo || selectedThreadId) return;
    if (nylasInbox.threads.length > 0) {
      setSelectedThreadId(nylasInbox.threads[0].id);
    }
  }, [isDemo, selectedThreadId, nylasInbox.threads.length]);

  // In prod mode, find the externalThreadId for the currently selected thread
  // so we can lazily fetch its messages.
  const selectedExternalThreadId = useMemo(() => {
    if (isDemo || !selectedThreadId) return null;
    const t = nylasInbox.threads.find((th) => th.id === selectedThreadId);
    return t?.externalThreadId ?? null;
  }, [isDemo, selectedThreadId, nylasInbox.threads]);

  const { messages: selectedThreadMessages } =
    useSelectedThreadMessages(
      isDemo ? null : nylasInbox.connectionId,
      selectedExternalThreadId,
      nylasInbox.connectionEmail
    );
  const [archivedIds, setArchivedIds] = useState<Set<string>>(() => new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(() => new Set());
  const [folderRailCollapsed, setFolderRailCollapsed] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [insightsCollapsed, setInsightsCollapsed] = useState(false);
  const [summaryOpenIds, setSummaryOpenIds] = useState<Set<string>>(() => new Set());
  const [smartReplyIds, setSmartReplyIds] = useState<Set<string>>(() => new Set());
  const [replyMode, setReplyMode] = useState<ReplyMode | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeState, setComposeState] = useState<ComposeState>(EMPTY_COMPOSE);
  const [dealTabs, setDealTabs] = useState<AriInboxThread[]>([]);
  const [activeDealTab, setActiveDealTab] = useState<"inbox" | string>("inbox");
  const [mobileDealRoom, setMobileDealRoom] = useState<AriInboxThread | null>(null);
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  // Tier C — thread-link recovery state. `recoveryBusy` tracks an in-flight
  // reprocess/reconnect by thread id so the action buttons disable + spin.
  // `reconnectThread` holds the thread whose reconnect deal-picker is open;
  // `pendingDeleteDeal` holds the {dealId, threadId} the delete confirm dialog
  // is gated on.
  const [recoveryBusy, setRecoveryBusy] = useState<string | null>(null);
  const [reconnectTarget, setReconnectTarget] = useState<AriInboxThread | null>(null);
  const [pendingDeleteDeal, setPendingDeleteDeal] = useState<{ dealId: string; threadId: string } | null>(null);
  const [deletingDeal, setDeletingDeal] = useState(false);
  const folderThreads = useMemo(() => {
    const source =
      activeFolder === "drafts"
        ? draftThreads
        : activeFolder === "sent"
          ? sentThreads
          : threads;

    return source
      .filter((thread) => !archivedIds.has(thread.id) && !deletedIds.has(thread.id))
      .filter((thread) => {
        if (activeFolder === "important") {
          return thread.starred || thread.ariAlerts.some((alert) => alert.urgency === "high");
        }
        return activeFolder !== "messages";
      })
      .filter((thread) => {
        if (viewMode === "notifications" && activeFolder !== "drafts" && activeFolder !== "sent") {
          return thread.ariAlerts.length > 0;
        }
        return true;
      })
      .filter((thread) => {
        if (viewMode !== "conversations" || activeFolder === "messages") {
          return true;
        }
        return threadMatchesDealTag(thread, activeDealTagFilter);
      })
      .filter((thread) => threadMatchesSearch(thread, search));
  }, [
    activeDealTagFilter,
    activeFolder,
    archivedIds,
    deletedIds,
    draftThreads,
    search,
    sentThreads,
    threads,
    viewMode,
  ]);

  const dealTagCounts = useMemo(() => {
    const counts = Object.fromEntries(
      ARI_INBOX_DEAL_TAG_FILTERS.map((tag) => [tag, 0])
    ) as Record<AriInboxDealTagFilter, number>;

    const pool =
      activeFolder === "drafts"
        ? draftThreads
        : activeFolder === "sent"
          ? sentThreads
          : threads;

    pool
      .filter((thread) => !archivedIds.has(thread.id) && !deletedIds.has(thread.id))
      .filter((thread) => {
        if (activeFolder === "important") {
          return thread.starred || thread.ariAlerts.some((alert) => alert.urgency === "high");
        }
        return activeFolder !== "messages";
      })
      .forEach((thread) => {
        thread.dealTags.forEach((tag) => {
          if (tag in counts) {
            counts[tag as AriInboxDealTagFilter] += 1;
          }
        });
      });

    return counts;
  }, [activeFolder, archivedIds, deletedIds, draftThreads, sentThreads, threads]);

  const filteredTextThreads = useMemo(
    () => textThreads.filter((thread) => textThreadMatchesSearch(thread, search)),
    [search, textThreads]
  );

  const allMailThreads = useMemo(
    () => [...threads, ...draftThreads, ...sentThreads],
    [draftThreads, sentThreads, threads]
  );

  // dealId of the currently-selected thread, when it carries a real deal link.
  // Drives the page-level deal fetch below so the reading pane (stage badge,
  // attachment strip) shows real deal data without each child re-fetching.
  const selectedThreadDealId = useMemo(() => {
    if (isDemo || !selectedThreadId) return null;
    return allMailThreads.find((t) => t.id === selectedThreadId)?.dealId ?? null;
  }, [isDemo, selectedThreadId, allMailThreads]);

  // Reuses the EXACT same SWR key DealRoomView uses (["deal-room", dealId]) so
  // the two callers dedupe to one /deals/get round-trip. Hydrates the selected
  // thread's pipeline.stage + attachments from real deal data.
  const { data: selectedDealData } = useSWR(
    selectedThreadDealId ? ["deal-room", selectedThreadDealId] : null,
    () => getDealApi({ dealId: selectedThreadDealId!, includeVersions: true }),
  );
  const selectedDealIntermediate = useDealIntermediateData(selectedDealData);

  const selectedThread = useMemo(() => {
    if (!selectedThreadId) return null;
    const thread = allMailThreads.find((t) => t.id === selectedThreadId) ?? null;
    if (!thread) return null;
    // In prod mode, inject lazily-loaded messages into the selected thread,
    // plus — for deal-linked threads — the real stage and real attachments
    // projected from the deal the thread points at. Unlinked / demo threads
    // keep their existing pipeline.stage and (empty) attachments untouched.
    if (!isDemo) {
      const hasMessages = selectedThreadMessages.length > 0;
      if (!thread.dealId) {
        return hasMessages ? { ...thread, messages: selectedThreadMessages } : thread;
      }
      return {
        ...thread,
        ...(hasMessages ? { messages: selectedThreadMessages } : {}),
        pipeline: {
          ...thread.pipeline,
          stage: mapDealStageToAriStage(latestDealStage(selectedDealData)),
        },
        attachments: dealFilesToAttachments(selectedDealIntermediate.files),
      };
    }
    return thread;
  }, [
    selectedThreadId,
    allMailThreads,
    isDemo,
    selectedThreadMessages,
    selectedDealData,
    selectedDealIntermediate.files,
  ]);
  const selectedTextThread =
    textThreads.find((thread) => thread.id === selectedTextThreadId) ??
    filteredTextThreads[0] ??
    null;
  const activeDealThread =
    activeDealTab === "inbox"
      ? null
      : dealTabs.find((thread) => thread.id === activeDealTab) ?? null;

  const unreadCount = threads.filter((thread) => thread.unread).length;
  const unreadTextCount = textThreads.filter((thread) => thread.unread).length;

  useEffect(() => {
    const dealRoomId = searchParams.get("dealRoom");
    if (!dealRoomId) return;

    const thread = allMailThreads.find((item) => item.id === dealRoomId);
    if (!thread) return;

    setActiveFolder("inbox");
    setSelectedThreadId(thread.id);
    setListCollapsed(true);

    if (isNarrow) {
      setMobileDealRoom(thread);
    } else {
      setDealTabs((current) =>
        current.some((tab) => tab.id === thread.id) ? current : [...current, thread]
      );
      setActiveDealTab(thread.id);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("dealRoom");
    setSearchParams(nextParams, { replace: true });
  }, [allMailThreads, isNarrow, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeFolder === "messages") return;
    if (!folderThreads.length) {
      setSelectedThreadId("");
      return;
    }
    if (!selectedThreadId) {
      if (!isNarrow) {
        setSelectedThreadId(folderThreads[0].id);
      }
      return;
    }
    if (!folderThreads.some((thread) => thread.id === selectedThreadId)) {
      setSelectedThreadId(folderThreads[0].id);
    }
  }, [activeFolder, folderThreads, isNarrow, selectedThreadId]);

  useEffect(() => {
    if (activeFolder !== "messages") return;
    if (!filteredTextThreads.length) {
      setSelectedTextThreadId("");
      return;
    }
    if (!filteredTextThreads.some((thread) => thread.id === selectedTextThreadId)) {
      setSelectedTextThreadId(filteredTextThreads[0].id);
    }
  }, [activeFolder, filteredTextThreads, selectedTextThreadId]);

  useEffect(() => {
    setReplyMode(null);
    setReplyBody("");
    setInsightsCollapsed(false);
  }, [selectedThreadId, activeFolder]);

  const updateSelectedThread = (updater: (thread: AriInboxThread) => AriInboxThread) => {
    if (!selectedThread) return;
    setThreads((current) => replaceThread(current, selectedThread.id, updater));
    setDraftThreads((current) => replaceThread(current, selectedThread.id, updater));
    setSentThreads((current) => replaceThread(current, selectedThread.id, updater));
  };

  const handleSelectThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    setActiveDealTab("inbox");
    // Demo path: mutate the local thread copy. Real path: setThreads is a no-op
    // (threads are the read-only Nylas cache), so record the open in readThreadIds
    // — the overlay above flips unread:false on render. Both paths clear the dot.
    setReadThreadIds((current) => {
      if (current.has(threadId)) return current;
      const next = new Set(current);
      next.add(threadId);
      return next;
    });
    setThreads((current) =>
      replaceThread(current, threadId, (thread) => ({ ...thread, unread: false }))
    );
    setDraftThreads((current) =>
      replaceThread(current, threadId, (thread) => ({ ...thread, unread: false }))
    );
    // Real route only: persist the read to Gmail via Nylas so the dot stays
    // cleared across reloads (the overlay above is optimistic-only). Fire-and-
    // forget — the UI already reflects read; a failed write just means the dot
    // reappears on the next full refetch, never blocks opening the thread. Only
    // fires for genuinely-unread real threads (skips demo + already-read).
    if (!isDemo && nylasInbox.connectionId) {
      const target = nylasInbox.threads.find((t) => t.id === threadId);
      if (target?.externalThreadId && target.unread) {
        void markThreadReadApi({
          connectionId: nylasInbox.connectionId,
          externalThreadId: target.externalThreadId,
          read: true,
        }).catch(() => {
          /* optimistic UI already updated; swallow — next refetch reconciles */
        });
      }
    }
  };

  const handleSelectTextThread = (threadId: string) => {
    setSelectedTextThreadId(threadId);
    setTextThreads((current) =>
      current.map((thread) => (thread.id === threadId ? { ...thread, unread: false } : thread))
    );
  };

  const handleArchive = (thread: AriInboxThread) => {
    setArchivedIds((current) => new Set(current).add(thread.id));
    toast.success("Archived");
  };

  const handleDelete = (thread: AriInboxThread) => {
    setDeletedIds((current) => new Set(current).add(thread.id));
    setDealTabs((current) => current.filter((tab) => tab.id !== thread.id));
    if (activeDealTab === thread.id) setActiveDealTab("inbox");
    toast.success("Deleted");
  };

  const handleToggleSummary = (threadId: string) => {
    setSummaryOpenIds((current) => {
      const next = new Set(current);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  };

  const handleToggleSmartReplies = (threadId: string) => {
    setSmartReplyIds((current) => {
      const next = new Set(current);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  };

  const handleReplySend = () => {
    if (!selectedThread || !replyBody.trim() || !replyMode) return;
    const now = new Date();
    const message: AriInboxMessage = {
      id: `ari-reply-${now.getTime()}`,
      sender: "You",
      email: "mayowa@equal-iq.com",
      date: "Just now",
      body: replyBody.trim(),
      preview: replyBody.trim().slice(0, 120),
      isCurrentUser: true,
    };
    updateSelectedThread((thread) => ({
      ...thread,
      messages: [...thread.messages, message],
      time: "Just now",
      date: "Today",
      unread: false,
    }));
    setReplyMode(null);
    setReplyBody("");
    toast.success(replyMode === "forward" ? "Forward sent" : "Reply sent");
  };

  const handleComposeSend = () => {
    if (!composeState.to.trim() || !composeState.body.trim()) {
      toast.error("Add a recipient and message before sending");
      return;
    }

    const now = Date.now();
    const subject = composeState.subject.trim() || "(no subject)";
    const sentThread: AriInboxThread = {
      id: `ari-sent-${now}`,
      unread: false,
      starred: false,
      from: {
        name: "You",
        email: "mayowa@equal-iq.com",
        initials: "MA",
      },
      to: [
        {
          name: composeState.to.trim(),
          email: composeState.to.trim(),
          initials: initialsFromName(composeState.to.trim()),
        },
      ],
      subject,
      date: "Today",
      time: "Just now",
      dealTags: [],
      clientTags: [],
      keywords: ["sent", "follow-up"],
      pipeline: { stage: "Prospecting", progress: 1 },
      messages: [
        {
          id: `ari-sent-msg-${now}`,
          sender: "You",
          email: "mayowa@equal-iq.com",
          date: "Just now",
          body: composeState.body.trim(),
          preview: composeState.body.trim().slice(0, 120),
          isCurrentUser: true,
        },
      ],
      ariAlerts: [],
      attachments: [],
      aiSummary: "Sent follow-up created from the Ari demo inbox.",
    };

    setSentThreads((current) => [sentThread, ...current]);
    setComposeOpen(false);
    setComposeState(EMPTY_COMPOSE);
    setActiveFolder("sent");
    setSelectedThreadId(sentThread.id);
    setActiveDealTab("inbox");
    toast.success("Email sent");
  };

  const handleOpenDealRoom = (thread: AriInboxThread) => {
    if (isNarrow) {
      setMobileDealRoom(thread);
      return;
    }
    setDealTabs((current) =>
      current.some((tab) => tab.id === thread.id) ? current : [...current, thread]
    );
    setActiveDealTab(thread.id);
  };

  const handleCloseDealTab = (threadId: string) => {
    setDealTabs((current) => current.filter((thread) => thread.id !== threadId));
    if (activeDealTab === threadId) {
      setActiveDealTab("inbox");
    }
  };

  // ─── Tier C — thread-link recovery handlers ──────────────────────────────
  //
  // All three route through the Deal Manager server-side (reprocess/reconnect
  // via the dealManagerWorkflow ops, delete via softDeleteDealUpdate). The FE
  // only fires the call, toasts the outcome, and revalidates the thread list so
  // the liveness projection re-runs and the row re-renders with its fresh
  // linkState.

  // Reprocess — mint a FRESH deal from the thread (orphaned thread, or a live
  // thread whose analysis failed). No un-delete: the backend clears the stale
  // ingest dedup and re-runs ingest into a brand-new deal.
  const handleReprocessThread = async (thread: AriInboxThread) => {
    if (!thread.externalThreadId) {
      toast.error("This thread can't be reprocessed (no external id).");
      return;
    }
    setRecoveryBusy(thread.id);
    try {
      await reprocessThreadApi({ externalThreadId: thread.externalThreadId, channel: "email" });
      toast.success("Reprocessing — a fresh deal is being created from this thread.");
      // Close any dead Deal Room tab for this thread; the fresh deal re-links on
      // the next projection.
      handleCloseDealTab(thread.id);
      if (mobileDealRoom?.id === thread.id) setMobileDealRoom(null);
      await nylasInbox.refetchThreads();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reprocess thread.");
    } finally {
      setRecoveryBusy(null);
    }
  };

  // Reconnect — repoint the thread's link at a chosen live deal (single-row
  // update server-side). The deal-picker dialog supplies newDealId.
  const handleReconnectThread = async (thread: AriInboxThread, newDealId: string) => {
    if (!thread.externalThreadId) {
      toast.error("This thread can't be reconnected (no external id).");
      return;
    }
    setRecoveryBusy(thread.id);
    try {
      await reconnectThreadApi({
        externalThreadId: thread.externalThreadId,
        channel: "email",
        newDealId,
      });
      toast.success("Thread reconnected to the selected deal.");
      setReconnectTarget(null);
      handleCloseDealTab(thread.id);
      if (mobileDealRoom?.id === thread.id) setMobileDealRoom(null);
      await nylasInbox.refetchThreads();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reconnect thread.");
    } finally {
      setRecoveryBusy(null);
    }
  };

  // Delete — soft-delete a live deal via the existing /deals/delete path
  // (useDealOperations.deleteDeal → Deal Manager). Deleting a deal turns its
  // threads ORPHANED; the projection surfaces that on the next revalidate so
  // the row flips to the recovery panel. Confirmation is gated by the
  // AlertDialog rendered at page level.
  const confirmDeleteDeal = async () => {
    if (!pendingDeleteDeal) return;
    setDeletingDeal(true);
    try {
      await dealOps.deleteDeal(pendingDeleteDeal.dealId);
      toast.success("Deal deleted.");
      handleCloseDealTab(pendingDeleteDeal.threadId);
      if (mobileDealRoom?.id === pendingDeleteDeal.threadId) setMobileDealRoom(null);
      setPendingDeleteDeal(null);
      await nylasInbox.refetchThreads();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete deal.");
    } finally {
      setDeletingDeal(false);
    }
  };

  const handleAskAri = () => {
    if (!chatInput.trim()) return;
    const userText = chatInput.trim();
    const thread = selectedThread;
    const alert = thread?.ariAlerts.find((item) => item.urgency === "high");
    const response = thread
      ? `${thread.subject}: ${alert?.text ?? thread.aiSummary} I would keep this in ${thread.pipeline.stage} and send a concise follow-up today.`
      : "I can help triage the selected thread once you choose a conversation.";

    setChatHistory((current) => [
      ...current,
      { role: "user", text: userText },
      { role: "ari", text: response },
    ]);
    setChatInput("");
  };

  const openSmartReplies = selectedThread ? smartReplyIds.has(selectedThread.id) : false;
  const smartReplies = selectedThread
    ? (ARI_SMART_REPLIES[selectedThread.id] ?? ARI_SMART_REPLIES.default)
    : [];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <FolderRail
          collapsed={folderRailCollapsed}
          activeFolder={activeFolder}
          activeDealTagFilter={activeDealTagFilter}
          dealTagCounts={dealTagCounts}
          onCollapse={() => setFolderRailCollapsed(true)}
          onExpand={() => setFolderRailCollapsed(false)}
          onFolderChange={(folder) => {
            setActiveFolder(folder);
            setActiveDealTab("inbox");
            setSearch("");
            if (folder === "messages") {
              setActiveDealTagFilter(null);
            }
          }}
          onDealTagFilterChange={setActiveDealTagFilter}
          unreadCount={unreadCount}
          unreadTextCount={unreadTextCount}
          onCompose={() => setComposeOpen(true)}
        />

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {activeFolder !== "messages" && (
            <InboxTabStrip
              dealTabs={dealTabs}
              activeDealTab={activeDealTab}
              listCollapsed={listCollapsed}
              onInbox={() => {
                setActiveDealTab("inbox");
                setListCollapsed(false);
              }}
              onSelectDealTab={setActiveDealTab}
              onCloseDealTab={handleCloseDealTab}
            />
          )}

          <div className="flex min-h-0 flex-1 overflow-hidden">
            {activeDealThread ? (
              // A thread with no openable deal (orphaned, cross-org, or R1-a
              // unlinked) has no real Deal Room — opening one would be a dead
              // room. Render the recovery panel (reprocess/reconnect) instead.
              // Live threads (and demo threads, which never carry a linkState)
              // get the real room.
              isThreadInRecovery(activeDealThread) ? (
                <ThreadRecoveryPanel
                  thread={activeDealThread}
                  mode="tab"
                  busy={recoveryBusy === activeDealThread.id}
                  onReprocess={() => handleReprocessThread(activeDealThread)}
                  onReconnect={() => setReconnectTarget(activeDealThread)}
                  onClose={() => handleCloseDealTab(activeDealThread.id)}
                />
              ) : (
                <DealRoomView
                  thread={activeDealThread}
                  mode="tab"
                  busy={recoveryBusy === activeDealThread.id}
                  onClose={() => handleCloseDealTab(activeDealThread.id)}
                  onRequestDelete={(dealId) =>
                    setPendingDeleteDeal({ dealId, threadId: activeDealThread.id })
                  }
                  onReprocess={() => handleReprocessThread(activeDealThread)}
                  onReconnect={() => setReconnectTarget(activeDealThread)}
                />
              )
            ) : activeFolder === "messages" ? (
              <MessagesWorkspace
                threads={filteredTextThreads}
                selectedThread={selectedTextThread}
                search={search}
                onSearch={setSearch}
                onSelectThread={handleSelectTextThread}
                onSend={(text) => {
                  if (!selectedTextThread || !text.trim()) return;
                  const time = new Date().toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  });
                  setTextThreads((current) =>
                    current.map((thread) =>
                      thread.id === selectedTextThread.id
                        ? {
                            ...thread,
                            lastAt: time,
                            preview: text.trim(),
                            unread: false,
                            messages: [
                              ...thread.messages,
                              {
                                id: `im-${Date.now()}`,
                                from: "me",
                                body: text.trim(),
                                time,
                              },
                            ],
                          }
                        : thread
                    )
                  );
                  toast.success("Sent via Messages");
                }}
              />
            ) : (
              <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
                <ThreadListPane
                  threads={folderThreads}
                  activeFolder={activeFolder}
                  viewMode={viewMode}
                  // Notifications is a demo-only tab: ariAlerts is populated
                  // only from the demo mock (ARI_INBOX_THREADS). On the real
                  // route ariAlerts is always empty, so the Notifications view
                  // would always read "No messages" — hide it there.
                  isDemo={isDemo}
                  search={search}
                  selectedThreadId={selectedThread?.id ?? null}
                  collapsed={listCollapsed}
                  // R1-a — the "Needs review / All mail" toggle is a prod-only,
                  // inbox-folder-only affordance (the unlinked threads it
                  // surfaces only exist on the real Nylas list). Hidden in demo
                  // mode and in drafts/sent so the deal-only default lens is
                  // untouched there.
                  showUnlinkedToggle={!isDemo && activeFolder === "inbox"}
                  includeUnlinked={includeUnlinked}
                  onToggleUnlinked={setIncludeUnlinked}
                  onCollapse={() => setListCollapsed(true)}
                  onExpand={() => setListCollapsed(false)}
                  onSearch={setSearch}
                  onViewModeChange={(mode) => {
                    setViewMode(mode);
                    if (mode === "notifications") {
                      setActiveDealTagFilter(null);
                    }
                  }}
                  onSelectThread={handleSelectThread}
                  onArchive={handleArchive}
                />

                {(!isNarrow || selectedThread) && (
                  <ReadingPane
                    thread={selectedThread}
                    showBackButton={isNarrow}
                    aiSummaryOpen={selectedThread ? summaryOpenIds.has(selectedThread.id) : false}
                    smartRepliesOpen={openSmartReplies}
                    smartReplies={smartReplies}
                    replyMode={replyMode}
                    replyBody={replyBody}
                    chatInput={chatInput}
                    chatHistory={chatHistory}
                    onBack={() => setSelectedThreadId("")}
                    onArchive={handleArchive}
                    onDelete={handleDelete}
                    onToggleSummary={handleToggleSummary}
                    onToggleSmartReplies={handleToggleSmartReplies}
                    onReplyModeChange={(mode) => {
                      setReplyMode((current) => (current === mode ? null : mode));
                      setReplyBody("");
                    }}
                    onReplyBodyChange={setReplyBody}
                    onReplySend={handleReplySend}
                    onUseSmartReply={(reply) => {
                      setReplyMode("reply");
                      setReplyBody(reply);
                    }}
                    onOpenDealRoom={handleOpenDealRoom}
                    onChatInput={setChatInput}
                    onAskAri={handleAskAri}
                  />
                )}

                <InsightsPanelShell
                  thread={selectedThread}
                  collapsed={insightsCollapsed}
                  onCollapse={() => setInsightsCollapsed(true)}
                  onExpand={() => setInsightsCollapsed(false)}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      <ComposeDialog
        open={composeOpen}
        value={composeState}
        onOpenChange={setComposeOpen}
        onChange={setComposeState}
        onSend={handleComposeSend}
      />

      <Sheet open={Boolean(mobileDealRoom)} onOpenChange={(open) => !open && setMobileDealRoom(null)}>
        <Sheet.Content className="w-[calc(100%-1rem)] max-w-3xl gap-0 bg-background p-0 text-foreground" side="right">
          <Sheet.Header className="border-b border-border pr-12">
            <Sheet.Title>Deal Room</Sheet.Title>
            <Sheet.Description>{mobileDealRoom?.subject}</Sheet.Description>
          </Sheet.Header>
          {mobileDealRoom &&
            (isThreadInRecovery(mobileDealRoom) ? (
              <ThreadRecoveryPanel
                thread={mobileDealRoom}
                mode="sheet"
                busy={recoveryBusy === mobileDealRoom.id}
                onReprocess={() => handleReprocessThread(mobileDealRoom)}
                onReconnect={() => setReconnectTarget(mobileDealRoom)}
                onClose={() => setMobileDealRoom(null)}
              />
            ) : (
              <DealRoomView
                thread={mobileDealRoom}
                mode="sheet"
                busy={recoveryBusy === mobileDealRoom.id}
                onClose={() => setMobileDealRoom(null)}
                onRequestDelete={(dealId) =>
                  setPendingDeleteDeal({ dealId, threadId: mobileDealRoom.id })
                }
                onReprocess={() => handleReprocessThread(mobileDealRoom)}
                onReconnect={() => setReconnectTarget(mobileDealRoom)}
              />
            ))}
        </Sheet.Content>
      </Sheet>

      {/* Tier C — reconnect deal-picker. Fed by /deals/list (the same
          useDealOperations the page already loads). Repoints the thread's link
          to the chosen live deal via /inbox/thread/reconnect. */}
      <ReconnectDealPicker
        open={Boolean(reconnectTarget)}
        deals={dealOps.deals}
        loading={dealOps.isLoading}
        busy={reconnectTarget ? recoveryBusy === reconnectTarget.id : false}
        onOpenChange={(open) => {
          if (!open) setReconnectTarget(null);
        }}
        onSelect={(dealId) => {
          if (reconnectTarget) void handleReconnectThread(reconnectTarget, dealId);
        }}
      />

      {/* Tier C — delete-deal confirm. Reuses /deals/delete via
          useDealOperations.deleteDeal (soft-delete through the Deal Manager).
          On confirm the deal's threads turn orphaned on the next projection. */}
      <AlertDialog
        open={Boolean(pendingDeleteDeal)}
        onOpenChange={(open) => {
          if (!open && !deletingDeal) setPendingDeleteDeal(null);
        }}
      >
        <AlertDialog.Popup>
          <AlertDialog.Header>
            <AlertDialog.Title>Delete this deal?</AlertDialog.Title>
            <AlertDialog.Description>
              The deal will be removed and this thread will become unlinked. You can
              reprocess the thread into a fresh deal or reconnect it to another deal
              afterward. This does not delete the email.
            </AlertDialog.Description>
          </AlertDialog.Header>
          <AlertDialog.Footer>
            <Button
              variant="outline"
              disabled={deletingDeal}
              onClick={() => setPendingDeleteDeal(null)}
            >
              Cancel
            </Button>
            <Button variant="destructive" disabled={deletingDeal} onClick={confirmDeleteDeal}>
              {deletingDeal ? "Deleting…" : "Delete deal"}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Popup>
      </AlertDialog>
    </div>
  );
}

function FolderRail({
  collapsed,
  activeFolder,
  activeDealTagFilter,
  dealTagCounts,
  onCollapse,
  onExpand,
  onFolderChange,
  onDealTagFilterChange,
  unreadCount,
  unreadTextCount,
  onCompose,
}: {
  collapsed: boolean;
  activeFolder: AriInboxFolder;
  activeDealTagFilter: AriInboxDealTagFilter | null;
  dealTagCounts: Record<AriInboxDealTagFilter, number>;
  onCollapse: () => void;
  onExpand: () => void;
  onFolderChange: (folder: AriInboxFolder) => void;
  onDealTagFilterChange: (tag: AriInboxDealTagFilter | null) => void;
  unreadCount: number;
  unreadTextCount: number;
  onCompose: () => void;
}) {
  const showTagFilters = activeFolder !== "messages";

  return (
    <aside
      className={cn(
        "hidden shrink-0 flex-col overflow-hidden border-r border-border bg-sidebar transition-[width] duration-300 ease-in-out md:flex",
        collapsed ? "w-11" : "w-60 2xl:w-64"
      )}
      aria-label="Inbox navigation"
      aria-expanded={!collapsed}
    >
      <div
        className={cn(
          "shrink-0 border-b border-border transition-[padding] duration-300 ease-in-out",
          collapsed ? "flex flex-col items-center gap-2 px-1.5 py-3" : "flex items-center gap-2 px-3 py-3"
        )}
      >
        {collapsed ? (
          <>
            <FolderRailIconTooltip label="Show navigation">
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Show navigation"
                onClick={onExpand}
              >
                <PanelLeft size={16} aria-hidden="true" />
              </Button>
            </FolderRailIconTooltip>
            <FolderRailIconTooltip label="Compose">
              <Button variant="outline" size="icon-sm" aria-label="Compose" onClick={onCompose}>
                <FileEdit size={16} aria-hidden="true" />
              </Button>
            </FolderRailIconTooltip>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="icon-sm"
              className="shrink-0"
              aria-label="Hide navigation"
              onClick={onCollapse}
            >
              <PanelLeftClose size={16} aria-hidden="true" />
            </Button>
            <Button className="min-w-0 flex-1 justify-center gap-2" size="sm" onClick={onCompose}>
              <FileEdit size={16} aria-hidden="true" className="shrink-0" />
              <span className="truncate">Compose</span>
            </Button>
          </>
        )}
      </div>

      <nav
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-y-auto transition-[padding] duration-300 ease-in-out",
          collapsed ? "items-center gap-1 px-1.5 py-3" : "space-y-5 px-2 py-3"
        )}
        aria-label="Demo inbox folders"
      >
        <FolderGroup label="Communication" collapsed={collapsed}>
          {ARI_INBOX_FOLDERS.map((folder) => {
            const isActive = activeFolder === folder.id;
            const badge =
              folder.id === "inbox"
                ? unreadCount
                : folder.id === "messages"
                  ? unreadTextCount
                  : folder.badge;
            const folderButton = (
              <button
                type="button"
                aria-label={
                  badge ? `${folder.label}, ${badge} unread` : folder.label
                }
                onClick={() => onFolderChange(folder.id)}
                className={cn(
                  "rounded-md transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                  collapsed
                    ? "relative flex size-8 items-center justify-center"
                    : "flex min-h-8 w-full items-center gap-2 px-2 text-left text-sm",
                  isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                <span className="shrink-0">{folderIcons[folder.id]}</span>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate transition-opacity duration-200 ease-in-out",
                    collapsed ? "sr-only" : "opacity-100"
                  )}
                >
                  {folder.label}
                </span>
                {Boolean(badge) &&
                  (collapsed ? (
                    <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                      {badge}
                    </span>
                  ) : (
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                      {badge}
                    </span>
                  ))}
              </button>
            );

            const tooltipLabel = badge ? `${folder.label} (${badge})` : folder.label;

            return collapsed ? (
              <FolderRailIconTooltip key={folder.id} label={tooltipLabel}>
                {folderButton}
              </FolderRailIconTooltip>
            ) : (
              <span key={folder.id} className="block w-full">
                {folderButton}
              </span>
            );
          })}
        </FolderGroup>

        {showTagFilters && (
          <div
            className={cn(
              "grid w-full transition-[grid-template-rows,opacity] duration-300 ease-in-out",
              collapsed
                ? "pointer-events-none grid-rows-[0fr] opacity-0"
                : "grid-rows-[1fr] opacity-100"
            )}
          >
            <div className="overflow-hidden">
              <FolderGroup label="Tags" collapsed={collapsed}>
                <button
                  type="button"
                  onClick={() => onDealTagFilterChange(null)}
                  className={cn(
                    "flex min-h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                    activeDealTagFilter === null
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">All deals</span>
                </button>
                {ARI_INBOX_DEAL_TAG_FILTERS.map((tagId) => {
                  const isActive = activeDealTagFilter === tagId;
                  const count = dealTagCounts[tagId];
                  return (
                    <button
                      key={tagId}
                      type="button"
                      onClick={() => onDealTagFilterChange(isActive ? null : tagId)}
                      className={cn(
                        "flex min-h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                        isActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <AriTag id={tagId} />
                      </span>
                      {count > 0 && (
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </FolderGroup>
            </div>
          </div>
        )}
      </nav>
    </aside>
  );
}

function FolderRailIconTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent side="right" align="center" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function FolderGroup({
  label,
  collapsed = false,
  children,
}: {
  label: string;
  collapsed?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("w-full", collapsed && "flex flex-col items-center")}>
      <div
        className={cn(
          "px-2 pb-1 text-xs font-medium text-muted-foreground/70 transition-opacity duration-200 ease-in-out",
          collapsed ? "h-0 overflow-hidden opacity-0" : "opacity-100"
        )}
      >
        {label}
      </div>
      <div className={cn("space-y-1", collapsed && "flex w-full flex-col items-center")}>
        {children}
      </div>
    </div>
  );
}

function InboxTabStrip({
  dealTabs,
  activeDealTab,
  listCollapsed,
  onInbox,
  onSelectDealTab,
  onCloseDealTab,
}: {
  dealTabs: AriInboxThread[];
  activeDealTab: "inbox" | string;
  listCollapsed: boolean;
  onInbox: () => void;
  onSelectDealTab: (tab: string) => void;
  onCloseDealTab: (threadId: string) => void;
}) {
  return (
    <div className="hidden h-9 shrink-0 items-end gap-1 border-b border-border px-3 lg:flex">
      <TabButton
        active={activeDealTab === "inbox"}
        icon={<Inbox className="size-4" aria-hidden="true" />}
        label={listCollapsed ? "Inbox hidden" : "Inbox"}
        onClick={onInbox}
      />
      {dealTabs.map((thread) => (
        <TabButton
          key={thread.id}
          active={activeDealTab === thread.id}
          icon={<Briefcase size={16} aria-hidden="true" />}
          label={`${thread.from.name} - Deal Room`}
          onClick={() => onSelectDealTab(thread.id)}
          onClose={() => onCloseDealTab(thread.id)}
        />
      ))}
    </div>
  );
}

function TabButton({
  active,
  icon,
  label,
  onClick,
  onClose,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  onClose?: () => void;
}) {
  return (
    <div
      className={cn(
        "flex h-8 max-w-64 min-w-0 items-center rounded-t-lg border text-sm transition-colors",
        active
          ? "border-border border-b-background bg-background text-foreground"
          : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {icon}
        <span className="truncate">{label}</span>
      </button>
      {onClose && (
        <button
          type="button"
          aria-label={`Close ${label}`}
          className="mr-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          <X className="size-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function ThreadListPane({
  threads,
  activeFolder,
  viewMode,
  isDemo,
  search,
  selectedThreadId,
  collapsed,
  showUnlinkedToggle,
  includeUnlinked,
  onToggleUnlinked,
  onCollapse,
  onExpand,
  onSearch,
  onViewModeChange,
  onSelectThread,
  onArchive,
}: {
  threads: AriInboxThread[];
  activeFolder: AriInboxFolder;
  viewMode: ViewMode;
  /** Demo route → show the Notifications tab (it has mock ariAlerts). On the
   *  real route ariAlerts is always empty, so Notifications is hidden. */
  isDemo: boolean;
  search: string;
  selectedThreadId: string | null;
  collapsed: boolean;
  /** R1-a — controls whether the "All Threads" toggle is enabled (prod inbox only). */
  showUnlinkedToggle: boolean;
  /** R1-a — current view: false = deal-only (default), true = all mail incl. unlinked. */
  includeUnlinked: boolean;
  onToggleUnlinked: (next: boolean) => void;
  onCollapse: () => void;
  onExpand: () => void;
  onSearch: (value: string) => void;
  onViewModeChange: (value: ViewMode) => void;
  onSelectThread: (threadId: string) => void;
  onArchive: (thread: AriInboxThread) => void;
}) {
  if (collapsed) {
    return (
      <div className="hidden w-10 shrink-0 flex-col items-center gap-3 border-r border-border bg-muted/30 pt-3 lg:flex">
        <Button variant="outline" size="icon-sm" aria-label="Show thread list" onClick={onExpand}>
          <PanelLeftOpen className="size-4" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <section
      className={cn(
        "flex w-full shrink-0 flex-col border-r border-border md:w-[300px] 2xl:w-[360px]",
        selectedThreadId && "max-lg:hidden"
      )}
      aria-label="Inbox thread list"
    >
      <div className="border-b border-border p-3">
        <div className="mb-3 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Input
              aria-label="Search messages"
              type="search"
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              startIcon={<Search aria-hidden="true" />}
              placeholder="Search messages..."
              size="sm"
              variant="muted"
              className="bg-muted/40 text-foreground"
            />
          </div>
          <Button
            className="hidden lg:inline-flex"
            variant="outline"
            size="icon-sm"
            aria-label="Hide thread list"
            onClick={onCollapse}
          >
            <PanelLeftClose className="size-4" aria-hidden="true" />
          </Button>
        </div>

        {activeFolder !== "drafts" && activeFolder !== "sent" && (
          <div className="flex items-center gap-2">
            {/* Always show the Conversations tab strip (matches the demo's
                header structure). Notifications is demo-only: its ariAlerts are
                populated solely from the demo mock, so on the real route the
                view is always empty — render that trigger only in demo, so the
                real route shows a single intentional Conversations pill rather
                than a confusing empty "No messages" tab. */}
            <Tabs value={viewMode} onValueChange={(value) => onViewModeChange(value as ViewMode)}>
              <Tabs.List variant="pill" indicatorColor="secondary" className="bg-muted/40">
                <Tabs.Trigger value="conversations" className="text-xs">
                  Conversations
                </Tabs.Trigger>
                {isDemo ? (
                  <Tabs.Trigger value="notifications" className="text-xs">
                    Notifications
                  </Tabs.Trigger>
                ) : null}
              </Tabs.List>
            </Tabs>
            {/* The "All Threads" toggle (formerly a dead Badge) flips the
                deal-only ↔ all-mail view. DEFAULT is OFF → deal-only, the
                sacred default: when off the inbox is byte-for-byte the
                deal-only list. ON surfaces unlinked, categorizer-ignored
                threads ("Needs review"). Enabled only on the prod inbox
                folder (showUnlinkedToggle); disabled in demo + drafts/sent +
                the messages folder, where there is no unlinked list to show. */}
            <Button
              type="button"
              variant={includeUnlinked ? "secondary" : "outline"}
              size="sm"
              disabled={!showUnlinkedToggle}
              aria-pressed={includeUnlinked}
              className="ml-auto h-7 px-3 text-xs"
              onClick={() => onToggleUnlinked(!includeUnlinked)}
            >
              All Threads
            </Button>
          </div>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="flex min-h-full flex-col gap-1 p-2">
          {threads.length === 0 ? (
            <EmptyPane icon={<Inbox className="size-6" />} title="No messages" body="Try another folder or search." />
          ) : (
            threads.map((thread) => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                selected={selectedThreadId === thread.id}
                notificationMode={viewMode === "notifications"}
                onSelect={() => onSelectThread(thread.id)}
                onArchive={() => onArchive(thread)}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </section>
  );
}

function ThreadRow({
  thread,
  selected,
  notificationMode,
  onSelect,
  onArchive,
}: {
  thread: AriInboxThread;
  selected: boolean;
  notificationMode: boolean;
  onSelect: () => void;
  onArchive: () => void;
}) {
  const firstAlert = thread.ariAlerts[0];
  const preview = notificationMode
    ? (firstAlert?.text ?? thread.aiSummary)
    : (lastItem(thread.messages)?.preview ?? thread.aiSummary);
  return (
    <div
      className={cn(
        "group flex w-full gap-3 rounded-xl px-3 py-3 text-left transition-colors",
        selected ? "bg-muted" : "hover:bg-muted/50",
        thread.unread && !selected && "bg-primary/0"
      )}
    >
      <span className="mt-1 size-2 shrink-0 rounded-full bg-transparent">
        {thread.unread && <span className="block size-2 rounded-full bg-destructive" />}
      </span>
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="mb-1 flex items-center gap-2">
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-sm",
              thread.unread ? "font-semibold text-foreground" : "font-medium text-foreground"
            )}
          >
            {notificationMode && firstAlert ? "Action needed: " : ""}
            {thread.subject}
          </span>
          {/* R3 — per-row attachment hint, anchored inline beside the time so it
              never strands on its own line when a thread has no tag chips. */}
          {thread.hasAttachments && (
            <Paperclip
              className="size-3.5 shrink-0 text-muted-foreground"
              aria-label="Has attachments"
            />
          )}
          <span className="shrink-0 text-xs text-muted-foreground">{thread.time}</span>
        </div>
        {/* Tag/badge row — only rendered when there's something to show, so a
            tag-less thread doesn't reserve an empty band (kept rows looking
            sparse vs the demo's dense chip rows). */}
        {(isThreadUnlinked(thread) || thread.dealTags.length > 0 || thread.clientTags.length > 0) && (
          <div className="mb-2 flex flex-wrap gap-1">
            {/* R1-a — flag unlinked (categorizer-ignored) threads so they're
                identifiable in the "Needs review" view. Only present when the
                backend projected linkState:"unlinked" (escape-hatch on). */}
            {isThreadUnlinked(thread) && (
              <Badge
                variant="outline"
                size="sm"
                className="rounded-full border-warning/40 bg-warning/10 px-2 py-0.5 text-xs font-semibold text-warning-foreground shadow-none"
              >
                Needs review
              </Badge>
            )}
            {thread.dealTags.slice(0, 2).map((tag) => (
              <AriTag key={tag} id={tag} />
            ))}
            {thread.clientTags.slice(0, 1).map((tag) => (
              <AriTag key={tag} id={tag} />
            ))}
          </div>
        )}
        <div className="line-clamp-1 text-xs text-muted-foreground">{preview}</div>
      </button>
      <button
        type="button"
        aria-label={`Archive ${thread.subject}`}
        className="mt-0.5 hidden shrink-0 self-start rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring group-hover:inline-flex group-focus-within:inline-flex"
        onClick={onArchive}
      >
        <Archive size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

function ReadingPane({
  thread,
  showBackButton,
  aiSummaryOpen,
  smartRepliesOpen,
  smartReplies,
  replyMode,
  replyBody,
  chatInput,
  chatHistory,
  onBack,
  onArchive,
  onDelete,
  onToggleSummary,
  onToggleSmartReplies,
  onReplyModeChange,
  onReplyBodyChange,
  onReplySend,
  onUseSmartReply,
  onOpenDealRoom,
  onChatInput,
  onAskAri,
}: {
  thread: AriInboxThread | null;
  showBackButton: boolean;
  aiSummaryOpen: boolean;
  smartRepliesOpen: boolean;
  smartReplies: string[];
  replyMode: ReplyMode | null;
  replyBody: string;
  chatInput: string;
  chatHistory: ChatMessage[];
  onBack: () => void;
  onArchive: (thread: AriInboxThread) => void;
  onDelete: (thread: AriInboxThread) => void;
  onToggleSummary: (threadId: string) => void;
  onToggleSmartReplies: (threadId: string) => void;
  onReplyModeChange: (mode: ReplyMode) => void;
  onReplyBodyChange: (value: string) => void;
  onReplySend: () => void;
  onUseSmartReply: (reply: string) => void;
  onOpenDealRoom: (thread: AriInboxThread) => void;
  onChatInput: (value: string) => void;
  onAskAri: () => void;
}) {
  const replyBoxRef = useRef<HTMLDivElement>(null);
  const shouldScrollToReplyRef = useRef(false);

  const handleReplyModeChange = (mode: ReplyMode) => {
    const closing = replyMode === mode;
    onReplyModeChange(mode);
    if (!closing) {
      shouldScrollToReplyRef.current = true;
    }
  };

  useEffect(() => {
    if (!replyMode || !shouldScrollToReplyRef.current) return;

    shouldScrollToReplyRef.current = false;
    requestAnimationFrame(() => {
      replyBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }, [replyMode]);

  if (!thread) {
    return (
      <section className="flex min-w-0 flex-1 items-center justify-center bg-background">
        <EmptyPane
          icon={<Mail className="size-7" />}
          title="Select a message"
          body="Choose a thread to view deal details, messages, and Ari insights."
        />
      </section>
    );
  }

  return (
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
      {/* Native scroll container (not base-ui ScrollArea) so the inner content's
          min-h-full resolves against the real viewport height — base-ui wraps
          children in a fit-content content element that breaks the fill chain,
          which left a dead gap below short emails. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col p-4 md:p-6">
          <PipelineBar thread={thread} onOpenDealRoom={() => onOpenDealRoom(thread)} />

          <div className="mb-4 flex items-start gap-3">
            {showBackButton && (
              <Button variant="outline" size="icon-sm" aria-label="Back to inbox" onClick={onBack}>
                <ChevronLeft size={16} aria-hidden="true" />
              </Button>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-balance text-lg font-semibold leading-tight text-foreground">
                {thread.subject}
              </h1>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[...thread.dealTags, ...thread.clientTags].map((tag) => (
                  <AriTag key={tag} id={tag} />
                ))}
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Archive message"
                onClick={() => onArchive(thread)}
              >
                <Archive size={16} aria-hidden="true" />
              </Button>
              <Button
                variant="destructive-outline"
                size="icon-sm"
                aria-label="Delete message"
                onClick={() => onDelete(thread)}
              >
                <Trash2 size={16} aria-hidden="true" />
              </Button>
            </div>
          </div>

          {aiSummaryOpen && (
            <section className="mb-4 rounded-2xl border border-primary/20 bg-primary/10 p-4">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
                <AriIcon variant="gradient" className="size-4 shrink-0" aria-hidden="true" />
                <TextShimmer as="span" duration={2} className="font-semibold">
                  AI Summary
                </TextShimmer>
              </div>
              <p className="text-pretty text-sm leading-6 text-foreground">{thread.aiSummary}</p>
            </section>
          )}

          {smartRepliesOpen && (
            <section className="mb-4">
              <div className="mb-2 text-xs font-semibold text-muted-foreground">Smart Replies</div>
              <div className="flex flex-wrap gap-2">
                {smartReplies.map((reply) => (
                  <Button
                    key={reply}
                    variant="outline"
                    size="sm"
                    rounded="full"
                    onClick={() => onUseSmartReply(reply)}
                  >
                    {reply}
                  </Button>
                ))}
              </div>
            </section>
          )}

          <div className="mb-5 flex flex-wrap items-center gap-2">
            <Button
              variant={aiSummaryOpen ? "secondary" : "outline"}
              size="sm"
              onClick={() => onToggleSummary(thread.id)}
            >
              <AriIcon variant="gradient" className="size-4 shrink-0" aria-hidden="true" />
              <TextShimmer 
                as="span" 
                repeatDelay={5} duration={2} className="[--base-color:#a1a1aa] [--base-gradient-color:#000] dark:[--base-color:#ffffff] dark:[--base-gradient-color:#6973ff]">
                AI Summary
              </TextShimmer>
            </Button>
            <Button
              variant={smartRepliesOpen ? "secondary" : "outline"}
              size="sm"
              onClick={() => onToggleSmartReplies(thread.id)}
            >
              Smart Reply
            </Button>
            <div className="hidden flex-1 sm:block" />
            <Button
              variant={replyMode === "reply" ? "secondary" : "outline"}
              size="sm"
              onClick={() => handleReplyModeChange("reply")}
            >
              <CornerDownLeft size={16} aria-hidden="true" />
              Reply
            </Button>
            <Button
              variant={replyMode === "replyAll" ? "secondary" : "outline"}
              size="sm"
              onClick={() => handleReplyModeChange("replyAll")}
            >
              Reply All
            </Button>
            <Button
              variant={replyMode === "forward" ? "secondary" : "outline"}
              size="sm"
              onClick={() => handleReplyModeChange("forward")}
            >
              <Send size={16} aria-hidden="true" />
              Forward
            </Button>
          </div>

          <AttachmentBar attachments={thread.attachments} />
          <MessageThread messages={thread.messages} />

          {replyMode && (
            <div ref={replyBoxRef}>
              <ReplyBox
                mode={replyMode}
                subject={thread.subject}
                value={replyBody}
                onChange={onReplyBodyChange}
                onCancel={() => handleReplyModeChange(replyMode)}
                onSend={onReplySend}
              />
            </div>
          )}
        </div>
      </div>

      <AriAskBar
        value={chatInput}
        history={chatHistory}
        onChange={onChatInput}
        onSend={onAskAri}
      />
    </section>
  );
}

function pipelineStageState(
  index: number,
  currentIndex: number,
): "complete" | "active" | "upcoming" {
  if (index < currentIndex) return "complete";
  if (index === currentIndex) return "active";
  return "upcoming";
}

function pipelineConnectorClass(connectorIndex: number, currentIndex: number) {
  return connectorIndex < currentIndex ? "bg-success" : "bg-muted";
}

function PipelineStageTrack({ currentIndex }: { currentIndex: number }) {
  const lastIndex = ARI_DEAL_STAGES.length - 1;

  return (
    <div className="grid grid-cols-6 gap-x-0">
      {ARI_DEAL_STAGES.map((stage, index) => {
        const state = pipelineStageState(index, currentIndex);
        return (
          <div key={stage} className="flex min-w-0 flex-col items-stretch">
            <div className="flex items-center">
              <div
                className={cn(
                  "h-0.5 min-w-2 flex-1",
                  index === 0 ? "invisible" : pipelineConnectorClass(index - 1, currentIndex),
                )}
                aria-hidden
              />
              <PipelineStageDot state={state} />
              <div
                className={cn(
                  "h-0.5 min-w-2 flex-1",
                  index === lastIndex ? "invisible" : pipelineConnectorClass(index, currentIndex),
                )}
                aria-hidden
              />
            </div>
            <div
              className={cn(
                "mt-1.5 truncate px-0.5 text-center text-xs",
                state === "active" && "font-semibold text-foreground",
                state !== "active" && "text-muted-foreground/70",
              )}
            >
              {stage}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PipelineStageDot({ state }: { state: "complete" | "active" | "upcoming" }) {
  if (state === "complete") {
    return (
      <span
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success"
        aria-hidden
      >
        <Check className="size-3 text-white" strokeWidth={2.5} />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground ring-2 ring-foreground/15"
        aria-hidden
      >
        <span className="size-1.5 rounded-full bg-background" />
      </span>
    );
  }
  return (
    <span
      className="size-5 shrink-0 rounded-full border-2 border-muted bg-transparent"
      aria-hidden
    />
  );
}

function PipelineBar({
  thread,
  onOpenDealRoom,
}: {
  thread: AriInboxThread;
  onOpenDealRoom: () => void;
}) {
  // A thread with no openable deal (orphaned, cross-org, or R1-a unlinked) has
  // no live tracker. Replace the dead "Go to Deal Room" tracker with a recovery
  // banner whose action routes to the recovery panel (which owns the
  // reprocess/reconnect buttons). Same onOpenDealRoom callback — the page-level
  // render gating decides room vs panel.
  if (isThreadInRecovery(thread)) {
    const isCrossOrg = thread.linkState === "cross-org";
    const isUnlinked = thread.linkState === "unlinked";
    const title = isUnlinked
      ? "Not yet linked to a deal"
      : isCrossOrg
        ? "Deal in another workspace"
        : "Deal no longer available";
    const action = isUnlinked ? "Process into deal" : "Recover deal link";
    // For an unlinked thread, lead with the categorizer's own reason (R0-b /
    // R1-a) so the user sees WHY the pipeline passed on it before deciding to
    // force-process. Fall back to generic copy when no reason was persisted.
    const body = isUnlinked
      ? thread.ignoreReason
        ? `The deal pipeline passed on this email: ${thread.ignoreReason}. Process it into a fresh deal to bring it into your dealflow.`
        : "The deal pipeline didn't link this email to a deal. Process it into a fresh deal to bring it into your dealflow."
      : isCrossOrg
        ? "This thread is linked to a deal in a workspace you can't open. Reconnect it to a deal in this workspace, or reprocess it into a fresh one."
        : "The deal this thread was linked to has been deleted. Reprocess it into a fresh deal, or reconnect it to an existing one.";
    return (
      <section className="mb-4 rounded-2xl border border-warning/40 bg-warning/5 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <AlertTriangle className="size-4 text-warning" aria-hidden="true" />
            <span className="text-xs font-semibold text-foreground">{title}</span>
          </div>
          <Button
            className="ml-auto"
            variant="outline"
            size="sm"
            rounded="full"
            onClick={onOpenDealRoom}
          >
            <RefreshCw className="size-4" aria-hidden="true" />
            {action}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{body}</p>
      </section>
    );
  }

  const currentIndex = ARI_DEAL_STAGES.indexOf(thread.pipeline.stage);
  return (
    <section className="mb-4 rounded-2xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <TrendingUp size={16} aria-hidden="true" className="text-foreground" />
          <span className="text-xs font-semibold text-foreground">Deal Tracker</span>
          <Badge variant="outline" className="border-transparent bg-muted text-muted-foreground">
            {thread.pipeline.stage}
          </Badge>
        </div>
        <Button className="ml-auto" size="sm" rounded="full" onClick={onOpenDealRoom}>
          Go to Deal Room
        </Button>
      </div>
      <PipelineStageTrack currentIndex={currentIndex} />
    </section>
  );
}

function AttachmentBar({ attachments }: { attachments: AriInboxAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        <Paperclip size={16} aria-hidden="true" />
        Attachments & Contracts
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {attachments.map((attachment) => (
          <button
            key={attachment.name}
            type="button"
            onClick={() => toast.info(`${attachment.name} is available in the demo vault`)}
            className="flex min-w-0 items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:border-foreground/20 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 text-foreground">
              <AttachmentTypeIcon type={attachment.type} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">{attachment.name}</div>
              <div className="text-xs text-muted-foreground">
                {attachment.size} - {attachment.date}
              </div>
            </div>
            <Badge className={attachmentToneClass(attachment.tone)}>
              {attachment.status}
            </Badge>
          </button>
        ))}
      </div>
    </section>
  );
}

function MessageThread({ messages }: { messages: AriInboxMessage[] }) {
  // A single HTML email should grow to fill the reading pane's remaining height
  // (so the body uses the space instead of leaving a gap above the Ari bar). A
  // multi-message thread keeps its natural stacked height and scrolls.
  const single = messages.length === 1;
  return (
    <section className={cn(
      "mb-5 overflow-hidden rounded-2xl border border-border bg-card",
      single && "flex flex-1 flex-col"
    )}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold text-foreground shrink-0">
        <MessageSquare size={16} aria-hidden="true" className="text-muted-foreground" />
        Conversation
        <span className="text-xs font-normal text-muted-foreground">({messages.length})</span>
      </div>
      <div className={cn("divide-y divide-border", single && "flex flex-1 flex-col min-h-0")}>
        {messages.map((message) => (
          <EmailMessage key={message.id} message={message} fill={single} />
        ))}
      </div>
    </section>
  );
}

function EmailMessage({ message, fill = false }: {
  message: AriInboxMessage;
  fill?: boolean;
}) {
  return (
    <article className={cn("px-4 py-4", message.isCurrentUser && "bg-muted/20", fill && "flex flex-1 flex-col min-h-0")}>
      <div className={cn("flex gap-3", fill && "flex-1 min-h-0")}>
        <Avatar
          className="size-8 border border-border bg-muted text-xs text-foreground shrink-0"
          fallback={message.isCurrentUser ? "MA" : initialsFromName(message.sender)}
          alt={message.sender}
        />
        <div className={cn("min-w-0 flex-1", fill && "flex flex-col min-h-0")}>
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 shrink-0">
            <span className="truncate text-sm font-semibold text-foreground">{message.sender}</span>
            <span className="truncate text-xs text-muted-foreground/70">
              &lt;{message.email}&gt;
            </span>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">{message.date}</span>
          </div>
          {message.bodyHtml ? (
            <EmailHtmlBody html={message.bodyHtml} fill={fill} />
          ) : (
            <p className="mt-3 whitespace-pre-wrap text-pretty text-sm leading-7 text-foreground">
              {message.body}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Renders a real email's HTML body safely. The body is email markup with its
 * OWN <style> blocks and inline CSS — rendering it inline would (a) dump CSS
 * as text under the old strip path, and (b) let the email's CSS bleed into and
 * break the app layout if injected as innerHTML.
 *
 * Two layers of defense:
 *   1. DOMPurify.sanitize() strips <script>, event handlers, and other
 *      script vectors before the markup ever reaches the DOM.
 *   2. A sandboxed <iframe> WITHOUT `allow-scripts` — so even if sanitization
 *      ever missed something, no script can run, and the iframe is its own CSS
 *      scope: the email's styles stay inside the frame and cannot touch the app.
 *
 * The iframe auto-sizes to its content height (bounded + scrollable) so long
 * emails don't blow out the reading pane.
 */
function EmailHtmlBody({ html, fill = false }: { html: string; fill?: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(200);

  // Sanitize once per html change. DOMPurify default config already drops
  // <script>, on* handlers, javascript: URLs, etc. We keep <style> so the
  // email renders as intended — it is harmless inside the sandboxed iframe.
  const srcDoc = useMemo(() => {
    const clean = DOMPurify.sanitize(html, {
      ADD_TAGS: ["style"],
      ADD_ATTR: ["target"],
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    });
    // The iframe is its own document and cannot inherit the app's theme, so we
    // make it TRANSPARENT — the conversation panel's real background shows
    // straight through, guaranteeing a match (no white box, no color guessing).
    // Default text is light to read on the dark panel; an email's own <style>
    // can still override for its own content. Reset margins + force responsive
    // images so an email can't force a huge fixed width. Links open in a new tab
    // (the iframe has no navigation rights without allow-top-navigation/popups).
    return `<!doctype html><html><head><meta charset="utf-8" />
<base target="_blank" />
<style>
  html,body{margin:0;padding:0;background:transparent;}
  body{font:14px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:rgba(255,255,255,0.85);word-break:break-word;overflow-x:hidden;}
  a{color:#8ab4ff;}
  img{max-width:100%;height:auto;}
  table{max-width:100%;}
</style>
</head><body>${clean}</body></html>`;
  }, [html]);

  // Measure the rendered content height and grow the iframe to fit it, so the
  // email body fills its natural space instead of being crammed into a small
  // box. A one-shot onLoad fires before images/fonts settle and under-measures,
  // so we re-measure on a short interval until the height stabilizes, and also
  // observe body resize where supported.
  const measure = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return 0;
    const h = Math.max(
      doc.body.scrollHeight,
      doc.documentElement?.scrollHeight ?? 0,
    );
    if (h > 0) setHeight(prev => (Math.abs(prev - h) > 2 ? h : prev));
    return h;
  }, []);

  const handleLoad = useCallback(() => {
    measure();
    const doc = iframeRef.current?.contentDocument;
    const win = iframeRef.current?.contentWindow;
    // Re-measure a few times as images/fonts load in (each can change height).
    let tries = 0;
    const id = win?.setInterval(() => {
      measure();
      if (++tries >= 10) win?.clearInterval(id);
    }, 150);
    // ResizeObserver catches later reflows (e.g. lazy images) where available.
    if (doc?.body && typeof (win as any)?.ResizeObserver !== "undefined") {
      const ro = new (win as any).ResizeObserver(() => measure());
      ro.observe(doc.body);
    }
  }, [measure]);

  return (
    <iframe
      ref={iframeRef}
      title="Email message body"
      // No allow-scripts: scripts can never run inside this frame. We grant
      // nothing else either, so the email cannot navigate the top frame.
      sandbox=""
      srcDoc={srcDoc}
      onLoad={handleLoad}
      // When `fill` (single-email thread), grow to fill the reading pane with the
      // measured content as a floor, so a short email uses the space instead of
      // leaving a gap above the Ari bar. Otherwise size to content height.
      className={cn(
        "mt-3 w-full rounded-md border border-border bg-transparent",
        fill && "flex-1 min-h-0"
      )}
      style={fill ? { minHeight: height } : { height }}
    />
  );
}

function ReplyBox({
  mode,
  subject,
  value,
  onChange,
  onCancel,
  onSend,
}: {
  mode: ReplyMode;
  subject: string;
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSend: () => void;
}) {
  return (
    <section className="mb-5 overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3 text-sm text-muted-foreground">
        {mode === "forward" ? `Fwd: ${subject}` : `Re: ${subject}`}
      </div>
      <div className="p-4">
        {mode === "forward" && (
          <Input
            aria-label="Forward recipient"
            placeholder="recipient@company.com"
            className="mb-3 bg-muted/40"
            variant="muted"
          />
        )}
        <Textarea
          aria-label="Reply body"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Write your reply..."
          className="bg-background"
        />
      </div>
      <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-3">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Attach file"
          onClick={() => toast.info("Attachment picker is simulated in demo mode")}
        >
          <Paperclip size={16} aria-hidden="true" />
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Discard
          </Button>
          <Button size="sm" onClick={onSend} disabled={!value.trim()}>
            <Send size={16} aria-hidden="true" />
            {mode === "forward" ? "Forward" : "Send Reply"}
          </Button>
        </div>
      </div>
    </section>
  );
}

function AriAskBar({
  value,
  history,
  onChange,
  onSend,
}: {
  value: string;
  history: ChatMessage[];
  onChange: (value: string) => void;
  onSend: () => void;
}) {
  const visibleHistory = history.slice(-4);
  return (
    <footer className="shrink-0 border-t border-border bg-card p-3">
      {visibleHistory.length > 0 && (
        <div className="mx-auto mb-3 flex max-h-36 max-w-4xl flex-col gap-2 overflow-y-auto">
          {visibleHistory.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={cn(
                "max-w-[82%] rounded-lg px-3 py-2 text-sm leading-5",
                message.role === "user"
                  ? "ml-auto bg-primary text-primary-foreground"
                  : "mr-auto border border-border bg-muted text-foreground"
              )}
            >
              {message.text}
            </div>
          ))}
        </div>
      )}
      <div className="mx-auto flex max-w-4xl items-center gap-2 rounded-full border border-border px-3 py-2 bg-transparent">
        <AriIcon variant="gradient" className="size-6 shrink-0" aria-hidden="true" />
        <Input
          variant="muted"
          aria-label="Ask Ari"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onSend();
            }
          }}
          placeholder="Ask Ari anything about your deals or your inbox..."
          className="flex-1 text-sm text-foreground w-full h-full bg-transparent"
        />
        <Button
          size="icon-sm"
          rounded="full"
          aria-label="Send Ari question"
          onClick={onSend}
          disabled={!value.trim()}
        >
          <Send size={16} aria-hidden="true" />
        </Button>
      </div>
    </footer>
  );
}

function InsightsPanelShell({
  thread,
  collapsed,
  onCollapse,
  onExpand,
}: {
  thread: AriInboxThread | null;
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
}) {
  if (!thread) return null;
  if (collapsed) {
    return (
      <aside className="hidden w-10 shrink-0 flex-col items-center gap-3 border-l border-border bg-muted/30 pt-3 xl:flex">
        <Button variant="outline" size="icon-sm" aria-label="Show insights panel" onClick={onExpand}>
          <PanelRightOpen className="size-4" aria-hidden="true" />
        </Button>
        <span className="text-xs font-medium text-muted-foreground/70 [writing-mode:vertical-rl]">
          Insights
        </span>
      </aside>
    );
  }
  return <InsightsPanel thread={thread} onCollapse={onCollapse} />;
}

function InsightsPanel({
  thread,
  onCollapse,
}: {
  thread: AriInboxThread;
  onCollapse: () => void;
}) {
  const client = ARI_CLIENT_PROFILES[thread.clientTags[0]];
  const counterparty = getAriCounterpartyByEmail(thread.from.email);
  const [mode, setMode] = useState<InsightMode>("client");

  useEffect(() => {
    setMode("client");
  }, [thread.id]);

  // Real (non-demo) threads carry no mock client/counterparty dossier — the
  // engine doesn't yet produce relationship scores, bios, social links, or
  // deal history. Rather than blank the whole right column (the old `return
  // null`), fall back to a real-data panel that surfaces what a live thread
  // genuinely has: the sender, the web-lookup buttons, the AI summary, deal
  // tags, keywords, and any Ari nudges. Same honest-empty discipline as the
  // analysis page's <NotExtractedYet> — show the structure, fill only what's
  // real, omit what the engine hasn't extracted.
  const hasMockDossier = Boolean(client || counterparty);
  const effectiveMode = client ? mode : "sender";

  return (
    <aside className="hidden w-72 shrink-0 flex-col border-l border-border bg-card xl:flex 2xl:w-120">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="text-sm font-semibold text-muted-foreground">Insights</div>
        <Button variant="ghost" size="icon-sm" aria-label="Hide insights panel" onClick={onCollapse}>
          <PanelRightClose className="size-4" aria-hidden="true" />
        </Button>
      </div>

      {client && counterparty && (
        <Tabs value={mode} onValueChange={(value) => setMode(value as InsightMode)}>
          <Tabs.List variant="pill" className="mx-4 mt-3 grid w-[calc(100%-2rem)] grid-cols-2 bg-muted/40" indicatorColor="secondary">
            <Tabs.Trigger value="client" className="text-xs">
              Your Client
            </Tabs.Trigger>
            <Tabs.Trigger value="sender" className="text-xs">
              Reaching Out
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs>
      )}

      <ScrollArea className="flex-1">
        {hasMockDossier ? (
          effectiveMode === "sender" && counterparty ? (
            <CounterpartyInsights counterparty={counterparty} thread={thread} />
          ) : client ? (
            <ClientInsights client={client} thread={thread} />
          ) : null
        ) : (
          <RealThreadInsights thread={thread} />
        )}
      </ScrollArea>
    </aside>
  );
}

/**
 * Insights for a real (non-demo) thread. No mock dossier exists, so this draws
 * only on fields the live thread actually carries. Every section is conditional
 * — when a field is empty the section is omitted rather than faked, so the panel
 * reads as honestly populated for the data we have (sender + web-lookup are
 * always present; AI summary / tags / keywords / nudges show only when real).
 */
function RealThreadInsights({ thread }: { thread: AriInboxThread }) {
  const senderName = thread.from.name?.trim() || thread.from.email;
  const lookupQuery = `${thread.from.name ?? ""} ${thread.from.email}`.trim();

  return (
    <div>
      <section className="border-b border-border p-4">
        <div className="mb-3 flex items-start gap-3">
          <Avatar
            fallback={thread.from.initials ?? senderName.slice(0, 2).toUpperCase()}
            alt={senderName}
            className="size-12 border border-border bg-muted text-foreground"
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">{senderName}</div>
            <div className="truncate text-xs leading-5 text-muted-foreground">{thread.from.email}</div>
          </div>
        </div>
        {thread.dealTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {thread.dealTags.map((tag) => (
              <AriTag key={tag} id={tag} />
            ))}
          </div>
        )}
      </section>

      <ProfileLookup query={lookupQuery} />

      {thread.aiSummary && (
        <section className="border-b border-border p-4">
          <div className="mb-2 text-xs font-semibold text-muted-foreground">AI Summary</div>
          <p className="text-pretty text-sm leading-6 text-muted-foreground">{thread.aiSummary}</p>
        </section>
      )}

      {thread.ariAlerts.length > 0 && (
        <section className="border-b border-border p-4">
          <AriNudges alerts={thread.ariAlerts} />
        </section>
      )}

      {thread.keywords.length > 0 && (
        <section className="p-4">
          <div className="mb-3 text-xs font-semibold text-muted-foreground">Topics</div>
          <div className="flex flex-wrap gap-1.5">
            {thread.keywords.map((keyword) => (
              <Badge
                key={keyword}
                variant="outline"
                size="sm"
                className="rounded-full px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-none"
              >
                {keyword}
              </Badge>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ClientInsights({
  client,
  thread,
}: {
  client: AriClientProfile;
  thread: AriInboxThread;
}) {
  const scoreClass =
    client.relationshipScore >= 90
      ? "text-success-foreground"
      : client.relationshipScore >= 80
        ? "text-warning-foreground"
        : "text-destructive-foreground";
  const profileLinks: LookupLink[] = [];
  if (client.social?.wikipedia) {
    profileLinks.push({
      href: client.social.wikipedia,
      label: "Wikipedia",
      icon: <Globe2 className="size-3.5" aria-hidden="true" />,
    });
  }
  if (client.social?.instagram) {
    profileLinks.push({
      href: client.social.instagram,
      label: "Instagram",
      icon: <Instagram className="size-3.5" aria-hidden="true" />,
    });
  }
  if (client.social?.spotify) {
    profileLinks.push({
      href: client.social.spotify,
      label: "Spotify",
      icon: <Users className="size-3.5" aria-hidden="true" />,
    });
  }

  return (
    <div>
      <section className="border-b border-border p-4">
        <div className="mb-3 flex items-start gap-3">
          <Avatar
            fallback={client.initials}
            alt={client.name}
            className="size-12 border border-border bg-muted text-foreground"
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{client.name}</div>
            <div className="text-pretty text-xs leading-5 text-muted-foreground">{client.title}</div>
          </div>
        </div>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {client.tags.map((tag) => (
            <AriTag key={tag} id={tag} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <InsightStat label="Outreaches" value={client.outreachCount} />
          <InsightStat label="Rel. Score" value={client.relationshipScore} valueClassName={scoreClass} />
          <InsightStat label="Deals" value={client.pastDeals.length} />
        </div>
      </section>

      <ProfileLookup
        query={`${client.name} ${client.title}`}
        links={profileLinks}
        sources={client.sources}
      />

      <section className="border-b border-border p-4">
        <div className="flex gap-2 text-sm text-foreground">
          <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />
          <span>{client.outreachNote}</span>
        </div>
      </section>

      <section className="border-b border-border p-4">
        <div className="mb-2 text-xs font-semibold text-muted-foreground">About</div>
        <p className="text-pretty text-sm leading-6 text-muted-foreground">{client.bio}</p>
      </section>

      <section className="border-b border-border p-4">
        <AriNudges alerts={thread.ariAlerts} />
      </section>

      <section className="p-4">
        <div className="mb-3 text-xs font-semibold text-muted-foreground">Deal History</div>
        <div className="space-y-2">
          {client.pastDeals.map((deal) => (
            <div key={`${deal.name}-${deal.year}`} className="rounded-2xl border border-border bg-muted/40 p-3">
              <div className="mb-1 flex items-center gap-2">
                <AriBrandOrFallback
                  brandId={resolveAriBrandId(deal.name)}
                  size="xs"
                  fallback={null}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {deal.name}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{deal.status}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground/70">
                <span>{deal.year}</span>
                <span>{deal.value}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function CounterpartyInsights({
  counterparty,
  thread,
}: {
  counterparty: AriCounterpartyProfile;
  thread: AriInboxThread;
}) {
  const primary =
    counterparty.contacts.find((contact) => contact.email === thread.from.email) ??
    counterparty.contacts.find((contact) => contact.primary) ??
    counterparty.contacts[0];
  const profileLinks: LookupLink[] = [
    {
      href: `https://${counterparty.domain}`,
      label: "Company Site",
      icon: <Globe2 className="size-3.5" aria-hidden="true" />,
    },
  ];

  return (
    <div>
      <section className="border-b border-border p-4">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-sm font-semibold text-foreground">
            {counterparty.initials}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{counterparty.company}</div>
            <div className="text-xs leading-5 text-muted-foreground">
              {counterparty.type} - {counterparty.hq}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <InsightStat label="Past Deals" value={counterparty.dealsWithUs} />
          <InsightStat label="Relationship" value={counterparty.relationship} />
        </div>
      </section>

      <ProfileLookup
        query={`${counterparty.company} ${primary?.name ?? ""} ${counterparty.hq}`}
        links={profileLinks}
      />

      <section className="border-b border-border p-4">
        <div className="mb-2 text-xs font-semibold text-muted-foreground">Primary Contact</div>
        {primary && (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
            <div className="font-medium text-foreground">{primary.name}</div>
            <div className="text-muted-foreground">{primary.title}</div>
            <div className="mt-1 truncate text-xs text-muted-foreground/70">{primary.email}</div>
          </div>
        )}
      </section>

      <section className="border-b border-border p-4">
        <div className="mb-2 text-xs font-semibold text-muted-foreground">About</div>
        <p className="text-pretty text-sm leading-6 text-muted-foreground">{counterparty.description}</p>
        <div className="mt-3 text-xs text-muted-foreground/70">Last deal: {counterparty.lastDeal}</div>
      </section>

      <section className="border-b border-border p-4">
        <div className="mb-2 text-xs font-semibold text-muted-foreground">Signals</div>
        <div className="space-y-2">
          {counterparty.signals.map((signal) => (
            <div key={signal.text} className="flex gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <span
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  signal.tone === "positive"
                    ? "bg-success"
                    : signal.tone === "warning"
                      ? "bg-warning"
                      : "bg-muted-foreground"
                )}
              />
              <span>{signal.text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="p-4">
        <div className="mb-2 text-xs font-semibold text-muted-foreground">News</div>
        <div className="space-y-2">
          {counterparty.news.map((item) => (
            <div key={item.title} className="rounded-lg border border-border bg-muted/40 p-3">
              <div className="text-sm text-foreground">{item.title}</div>
              <div className="mt-1 text-xs text-muted-foreground/70">{item.date}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function InsightStat({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-lg bg-muted/40 p-3 text-center">
      <div className={cn("truncate text-base font-semibold text-foreground", valueClassName)}>
        {value}
      </div>
      <div className="mt-1 text-xs text-muted-foreground/70">{label}</div>
    </div>
  );
}

function AriNudges({ alerts }: { alerts: AriInsightAlert[] }) {
  if (!alerts.length) return null;
  return (
    <div>
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold">
        <Sparkles size={16} aria-hidden="true" />
        Ari's Insights
      </div>
      <div className="space-y-2">
        {alerts.map((alert) => (
          <div key={alert.text} className={cn("flex gap-3 rounded-2xl border p-3", alertClass(alert.urgency))}>
            <span className={cn("mt-1 size-2 shrink-0 rounded-full", alertDotClass(alert.urgency))} />
            <p className="text-pretty text-sm leading-6 text-foreground">{alert.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileLookup({
  query,
  links = [],
  sources = [],
}: {
  query: string;
  links?: LookupLink[];
  sources?: Array<{ title: string; url: string }>;
}) {
  const allLinks: LookupLink[] = [
    { href: webSearchUrl(query), label: "Web Search", icon: <Search className="size-3.5" aria-hidden="true" /> },
    ...links,
  ];

  return (
    <section className="border-b border-border p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        <Search className="size-4" aria-hidden="true" />
        Web Lookup & Profiles
      </div>
      <div className="flex flex-wrap gap-2">
        {allLinks.map((link) => (
          <Button
            key={`${link.label}-${link.href}`}
            variant="outline"
            size="xs"
            render={<a href={link.href} target="_blank" rel="noreferrer" />}
          >
            {link.icon ?? <ExternalLink className="size-3.5" aria-hidden="true" />}
            {link.label}
          </Button>
        ))}
      </div>
      {sources.length > 0 && (
        <div className="mt-4 space-y-2">
          {sources.map((source, index) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="flex gap-2 text-xs leading-5 text-muted-foreground/70 hover:text-primary"
            >
              <span>[{index + 1}]</span>
              <span>{source.title}</span>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

function MessagesWorkspace({
  threads,
  selectedThread,
  search,
  onSearch,
  onSelectThread,
  onSend,
}: {
  threads: AriTextThread[];
  selectedThread: AriTextThread | null;
  search: string;
  onSearch: (value: string) => void;
  onSelectThread: (threadId: string) => void;
  onSend: (text: string) => void;
}) {
  return (
    <>
      <section
        className={cn(
          "flex w-full shrink-0 flex-col border-r border-border md:w-[340px]",
          selectedThread && "max-lg:hidden"
        )}
      >
        <div className="border-b border-border p-3">
          <div className="mb-3 flex items-center gap-2">
            <MessageSquare className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2 className="text-sm font-semibold">Messages</h2>
          </div>
          <Input
            aria-label="Search text messages"
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            startIcon={<Search aria-hidden="true" />}
            placeholder="Search texts..."
            size="sm"
            variant="muted"
            className="bg-muted/40 text-foreground"
          />
          <p className="mt-2 text-xs leading-5 text-muted-foreground/70">
            iMessage threads where Ari is reachable through the workspace relay.
          </p>
        </div>
        <ScrollArea className="flex-1">
          <div className="flex min-h-full flex-col gap-1 p-2">
            {threads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => onSelectThread(thread.id)}
                className={cn(
                  "flex w-full gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring",
                  selectedThread?.id === thread.id && "bg-muted"
                )}
              >
                <span className="mt-2 size-2 shrink-0 rounded-full">
                  {thread.unread && <span className="block size-2 rounded-full bg-destructive" />}
                </span>
                <Avatar
                  fallback={thread.initials}
                  alt={thread.title}
                  className="size-9 border border-border bg-muted text-foreground"
                />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                      {thread.title}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground/70">{thread.lastAt}</span>
                  </div>
                  {thread.isAriThread && (
                    <div className="mb-1 text-xs font-semibold text-primary">Direct to Ari</div>
                  )}
                  <p className="truncate text-xs text-muted-foreground">{thread.preview}</p>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </section>
      <MessageConversation thread={selectedThread} onSend={onSend} />
    </>
  );
}

function MessageConversation({
  thread,
  onSend,
}: {
  thread: AriTextThread | null;
  onSend: (text: string) => void;
}) {
  const [draft, setDraft] = useState("");
  if (!thread) {
    return (
      <section className="flex flex-1 items-center justify-center">
        <EmptyPane icon={<MessageSquare className="size-7" />} title="Select a conversation" body="Choose a message thread to continue." />
      </section>
    );
  }

  return (
    <section className="flex min-w-0 flex-1 flex-col bg-background relative overflow-auto pb-36">
      <header className="flex items-center gap-3 border-b border-border bg-card p-4">
        <Avatar
          fallback={thread.initials}
          alt={thread.title}
          className="size-10 border border-border bg-muted text-foreground"
        />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold text-foreground">{thread.title}</h1>
          <p className="truncate text-sm text-muted-foreground">{thread.subtitle}</p>
        </div>
        <Badge variant="outline" className="border-border text-muted-foreground">
          iMessage
        </Badge>
      </header>
      <ScrollArea className="flex-1">
        <div className="flex min-h-full flex-col gap-3 p-4">
          {thread.messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "max-w-[78%]",
                message.from === "me" ? "ml-auto text-right" : "mr-auto text-left"
              )}
            >
              {message.from !== "me" && (
                <div className="mb-1 px-1 text-xs font-medium text-muted-foreground/70">
                  {message.from === "ari" ? "Ari" : message.sender}
                </div>
              )}
              <div
                className={cn(
                  "rounded-2xl px-4 py-2 text-sm leading-6",
                  message.from === "me"
                    ? "rounded-xl bg-foreground text-background"
                    : message.from === "ari"
                      ? "rounded-xl bg-muted text-foreground"
                      : "rounded-xl border border-border bg-card text-foreground"
                )}
              >
                {message.body}
              </div>
              <div className="mt-1 px-1 text-xs text-muted-foreground/70">{message.time}</div>
            </div>
          ))}
        </div>
      </ScrollArea>
      <footer className="border-t border-border bg-card p-4 absolute bottom-0 left-0 right-0">
        
        <div className="flex items-end gap-2">
          <Textarea
            aria-label="Send an iMessage"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (draft.trim()) {
                  onSend(draft);
                  setDraft("");
                }
              }
            }}
            placeholder={thread.isAriThread ? "iMessage Ari..." : "Send an iMessage..."}
            className="bg-muted/40 h-8 overflow-hidden"
          />
          <Button
            onClick={() => {
              onSend(draft);
              setDraft("");
            }}
            disabled={!draft.trim()}
          >
            <Send size={16} aria-hidden="true" />
            Send
          </Button>
        </div>

        <p className="mt-2 text-xs text-muted-foreground/70 text-center">
          Texts sync through your connected Apple relay. Ari replies here and in Ask Ari.
        </p>
      </footer>
    </section>
  );
}

function DealRoomView({
  thread,
  mode,
  busy,
  onClose,
  onRequestDelete,
  onReprocess,
  onReconnect,
}: {
  thread: AriInboxThread;
  mode: "tab" | "sheet";
  /** True while a reprocess/reconnect/delete for this thread is in flight. */
  busy?: boolean;
  onClose: () => void;
  /** Tier C — open the delete-deal confirm for the real linked deal. Absent in
   *  demo mode / when the thread carries no dealId. */
  onRequestDelete?: (dealId: string) => void;
  /** Tier C — reprocess this thread into a fresh deal (offered when analysis
   *  failed on the live deal). */
  onReprocess?: () => void;
  /** Tier C — open the reconnect deal-picker for this thread. */
  onReconnect?: () => void;
}) {
  const [tab, setTab] = useState("overview");
  const linkedContract = getAriThreadLinkedContract(thread.id);

  // (a) Foundation — resolve the real deal this thread is linked to. Only a
  // thread that genuinely carries a dealId (projected by the backend) gets
  // the real-data path; everything else (today: all live + demo threads,
  // since no projection sets dealId) keeps the existing thread-based
  // rendering. Weakest-correct: the real DocumentsTable / activity / notes
  // light up automatically the moment a thread→deal link appears, without
  // touching unlinked or demo threads.
  const dealId = thread.dealId ?? null;
  const { data: apiDealData } = useSWR(
    dealId ? ["deal-room", dealId] : null,
    () => getDealApi({ dealId: dealId!, includeVersions: true }),
  );
  const intermediate = useDealIntermediateData(apiDealData);

  // Tier C — a live deal whose contract analysis failed is a recovery case too:
  // offer Reprocess (re-run ingest → fresh deal) right in the room. The engine
  // surfaces a terminal failure as a "failed"/"error" analysis status; match
  // case-insensitively so an engine status-string rename doesn't silently hide
  // the affordance.
  const analysisFailed = !!(
    intermediate.analysisStatus &&
    /fail|error/i.test(intermediate.analysisStatus)
  );

  // (a) Real stage — a deal-linked thread reads its current stage from the
  // deal's latest version (same derivation as DealPage), instead of the
  // hardcoded "Prospecting" baked into every projected thread. Unlinked /
  // demo threads keep thread.pipeline.stage untouched.
  const stage: AriDealStage = dealId
    ? mapDealStageToAriStage(latestDealStage(apiDealData))
    : thread.pipeline.stage;
  const stageIndex = ARI_DEAL_STAGES.indexOf(stage);

  // Real files projected into the DemoDocument shape the production
  // DocumentsTable renders — same projection DealPage uses so the two
  // surfaces stay in lockstep.
  const dealDocuments = useMemo(
    () =>
      intermediate.files.map((f) => ({
        id: f.fileId,
        name: f.fileName,
        type: (f.fileType ?? "").includes("pdf") ? "PDF" : (f.fileType ?? "File"),
        // Real files have no per-document review status in the engine yet —
        // surface as "draft" until contract-state extraction is wired.
        status: "draft" as const,
        linkedAnalysis: !!intermediate.primaryContractId,
        uploadedAt: f.createdAt ?? "",
        updatedAt: f.createdAt ?? "",
        size: fileSizeLabel(f.sizeBytes),
        uploadedBy:
          f.sourceChannel === "email"
            ? "Email"
            : f.sourceChannel === "imessage"
              ? "iMessage"
              : "—",
      })),
    [intermediate.files, intermediate.primaryContractId],
  );

  return (
    <section className="flex min-h-0 flex-1 flex-col bg-background">
      {mode === "tab" && (
        <header className="flex items-start gap-3 border-b border-border bg-card p-5">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground/70">Deal Tracker</span>
              <Badge variant="outline" className={stageClasses[stage]}>
                {stage}
              </Badge>
            </div>
            <h1 className="truncate text-xl font-semibold text-foreground">{thread.subject}</h1>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              <div className="flex flex-wrap gap-1.5">
                {[...thread.dealTags, ...thread.clientTags].map((tag) => (
                  <AriTag key={tag} id={tag} />
                ))}
              </div>
              {linkedContract && (
                <Link
                  to={`${PATH_ROOT.demo.analysis}/${linkedContract.contractId}`}
                  className="shrink-0 text-right text-primary text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                >
                  View linked contract
                </Link>
              )}
            </div>
          </div>
          {/* Tier C — deal-room actions. Reprocess shows only when analysis
              failed; Delete is always available on a real linked deal. */}
          <div className="flex shrink-0 items-center gap-2">
            {dealId && analysisFailed && onReprocess && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onReprocess}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="size-4" aria-hidden="true" />
                )}
                Reprocess
              </Button>
            )}
            {dealId && onRequestDelete && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={busy}
                onClick={() => onRequestDelete(dealId)}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </Button>
            )}
            <Button variant="ghost" size="icon-sm" aria-label="Close deal room" onClick={onClose}>
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </header>
      )}
      {/* Sheet mode has no header — surface the same actions as a compact row so
          mobile deal rooms aren't dead-ends for delete/reprocess. */}
      {mode === "sheet" && dealId && (onRequestDelete || (analysisFailed && onReprocess)) && (
        <div className="flex items-center justify-end gap-2 border-b border-border bg-card px-5 py-3">
          {analysisFailed && onReprocess && (
            <Button variant="outline" size="sm" disabled={busy} onClick={onReprocess}>
              {busy ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="size-4" aria-hidden="true" />
              )}
              Reprocess
            </Button>
          )}
          {onRequestDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={busy}
              onClick={() => onRequestDelete(dealId)}
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Delete
            </Button>
          )}
        </div>
      )}
      <div className="border-b border-border bg-muted/30 px-5 py-3">
        <PipelineStageTrack currentIndex={stageIndex} />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <Tabs.List variant="pill" className="mx-5 mt-4 bg-muted/40" indicatorColor="secondary">
          {["overview", "documents", "timeline", "notes"].map((item) => (
            <Tabs.Trigger key={item} value={item} className="capitalize">
              {item}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs>
      <ScrollArea className="flex-1">
        <div className="p-5">
          {tab === "overview" && <DealRoomOverview thread={thread} />}
          {/* (b) Documents — real production DocumentsTable when the thread is
              linked to a deal; AttachmentBar fallback for unlinked threads. */}
          {tab === "documents" &&
            (dealId ? (
              dealDocuments.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">No documents yet</p>
              ) : (
                <DocumentsTable documents={dealDocuments} />
              )
            ) : (
              <AttachmentBar attachments={thread.attachments} />
            ))}
          {/* (c) Timeline — real activity events when linked; thread-derived
              fallback for unlinked threads. */}
          {tab === "timeline" &&
            (dealId ? (
              <DealRoomTimeline events={intermediate.activityEvents} />
            ) : (
              <DealRoomTimeline thread={thread} />
            ))}
          {/* (d) Notes — real persisted deal-thread note when linked;
              local-only Textarea fallback for unlinked threads. */}
          {tab === "notes" &&
            (dealId ? (
              <DealRoomNotes dealId={dealId} />
            ) : (
              <Textarea
                aria-label="Deal notes"
                placeholder="Add notes about this deal..."
                className="bg-card"
              />
            ))}
        </div>
      </ScrollArea>
    </section>
  );
}

/**
 * Tier C — recovery panel shown in place of a Deal Room for a thread whose deal
 * link is orphaned (deal soft-deleted) or cross-org (deal in a workspace the
 * caller can't open). Owns the two recovery actions:
 *   • Reprocess — mint a FRESH deal from the thread (re-runs ingest server-side;
 *     no resurrection of the deleted deal).
 *   • Reconnect — repoint the thread's link at a chosen live deal (opens the
 *     deal-picker the parent renders).
 * Cross-org threads never carry a priorDealId (the backend withholds another
 * org's id), so the panel only ever shows the deleted-deal id for in-org
 * orphans.
 */
function ThreadRecoveryPanel({
  thread,
  mode,
  busy,
  onReprocess,
  onReconnect,
  onClose,
}: {
  thread: AriInboxThread;
  mode: "tab" | "sheet";
  busy: boolean;
  onReprocess: () => void;
  onReconnect: () => void;
  onClose: () => void;
}) {
  const isCrossOrg = thread.linkState === "cross-org";
  const isUnlinked = thread.linkState === "unlinked";
  // Per-state copy. Unlinked (R1-a) is the never-linked-email case: the panel
  // leads with the categorizer's reason (R0-b persisted it) so the user sees
  // why the deal pipeline passed on it, and the primary action is force-process
  // ("Process into deal" — reprocess now accepts an unlinked thread, R1-b).
  const heading = isUnlinked
    ? "This email isn't linked to a deal"
    : isCrossOrg
      ? "Deal lives in another workspace"
      : "This deal was deleted";
  const detail = isUnlinked
    ? thread.ignoreReason
      ? `The deal pipeline passed on this email: ${thread.ignoreReason}. Process it into a fresh deal to bring it into your dealflow — the email itself is untouched.`
      : "The deal pipeline didn't link this email to a deal. Process it into a fresh deal to bring it into your dealflow — the email itself is untouched."
    : isCrossOrg
      ? "This thread is linked to a deal in a workspace you can't open from here. You can reprocess the thread into a fresh deal in this workspace, or reconnect it to a deal you can access."
      : "The deal this thread was linked to has been deleted. Reprocess the thread into a fresh deal, or reconnect it to an existing one — the email itself is untouched.";
  const primaryAction = isUnlinked ? "Process into deal" : "Reprocess into fresh deal";
  return (
    <section className="flex min-h-0 flex-1 flex-col bg-background">
      {mode === "tab" && (
        <header className="flex items-start gap-3 border-b border-border bg-card p-5">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="size-4 text-warning" aria-hidden="true" />
              <span className="text-xs font-semibold text-muted-foreground/70">
                {isUnlinked ? "Needs review" : "Deal link needs attention"}
              </span>
            </div>
            <h1 className="truncate text-xl font-semibold text-foreground">{thread.subject}</h1>
          </div>
          <Button variant="ghost" size="icon-sm" aria-label="Close panel" onClick={onClose}>
            <X className="size-4" aria-hidden="true" />
          </Button>
        </header>
      )}
      <ScrollArea className="flex-1">
        <div className="mx-auto w-full max-w-2xl p-6">
          <div className="rounded-2xl border border-warning/40 bg-warning/5 p-6">
            <div className="mb-3 flex items-center gap-2">
              {isCrossOrg ? (
                <Lock className="size-5 text-warning" aria-hidden="true" />
              ) : (
                <AlertTriangle className="size-5 text-warning" aria-hidden="true" />
              )}
              <h2 className="text-base font-semibold text-foreground">{heading}</h2>
            </div>
            <p className="text-sm leading-6 text-muted-foreground">{detail}</p>
            {isUnlinked && thread.categorizerVerdict && (
              <p className="mt-2 text-xs text-muted-foreground/70">
                Categorizer verdict:{" "}
                <span className="font-mono">{thread.categorizerVerdict}</span>
                {thread.categorizerConfidence !== undefined && (
                  <> (confidence {Math.round(thread.categorizerConfidence * 100)}%)</>
                )}
              </p>
            )}
            {!isCrossOrg && !isUnlinked && thread.priorDealId && (
              <p className="mt-2 text-xs text-muted-foreground/70">
                Previously linked to deal{" "}
                <span className="font-mono">{thread.priorDealId.slice(0, 8)}</span>
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-3">
              <Button disabled={busy} onClick={onReprocess}>
                {busy ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="size-4" aria-hidden="true" />
                )}
                {primaryAction}
              </Button>
              {/* Reconnect repoints an EXISTING DealThreadLink — the backend
                  reconnect handler 404s when no link exists. An unlinked
                  (never-linked) thread has no link to repoint, so reconnect is
                  not offered there; force-process (reprocess) is its create
                  path (R1-b). Orphaned/cross-org threads still have a link, so
                  they keep the reconnect affordance (Tier C). */}
              {!isUnlinked && (
                <Button variant="outline" disabled={busy} onClick={onReconnect}>
                  <Link2 className="size-4" aria-hidden="true" />
                  Reconnect to a deal
                </Button>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>
    </section>
  );
}

/**
 * Tier C — deal-picker for Reconnect. Lists the caller's deals (fed by
 * /deals/list via the page's useDealOperations) and repoints the orphaned/cross
 * -org thread's link to the chosen deal. The actual relink + RBAC re-check
 * happen server-side (/inbox/thread/reconnect → Deal Manager); the picker only
 * supplies newDealId.
 */
function ReconnectDealPicker({
  open,
  deals,
  loading,
  busy,
  onOpenChange,
  onSelect,
}: {
  open: boolean;
  deals: DealWithTitle[];
  loading: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (dealId: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter((d) =>
      (d.title || d.dealId).toLowerCase().includes(q),
    );
  }, [deals, filter]);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <Dialog.Popup className="max-w-lg bg-background p-0 text-foreground" showCloseButton={false}>
        <Dialog.Header className="border-b border-border px-5 py-4">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base">Reconnect to a deal</Dialog.Title>
            <Dialog.Close
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Close deal picker"
            >
              <X className="size-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-xs text-muted-foreground">
            Choose the deal this thread should be linked to.
          </Dialog.Description>
        </Dialog.Header>
        <div className="border-b border-border px-5 py-3">
          <Input
            aria-label="Filter deals"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search deals…"
            className="text-sm text-foreground"
          />
        </div>
        <ScrollArea className="max-h-80">
          <div className="p-2">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Loading deals…
              </div>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                {deals.length === 0
                  ? "No deals in this workspace yet. Reprocess the thread to create one."
                  : "No deals match your search."}
              </p>
            ) : (
              filtered.map((deal) => (
                <button
                  key={deal.dealId}
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect(deal.dealId)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  <BriefcaseBusiness className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{deal.title}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground/70">
                      {deal.dealId.slice(0, 8)}
                    </div>
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Dialog.Popup>
    </Dialog>
  );
}

function DealRoomOverview({ thread }: { thread: AriInboxThread }) {
  const latestMessage = lastItem(thread.messages);
  const nextAction = thread.ariAlerts[0]?.text ?? "No immediate action flagged.";

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_260px]">
      <section>
        <div className="mb-3 text-xs font-semibold text-muted-foreground">Latest Message</div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-2 text-sm text-muted-foreground">
            {latestMessage?.sender} - {latestMessage?.date}
          </div>
          <p className="line-clamp-6 whitespace-pre-wrap text-pretty text-sm leading-7 text-foreground">
            {latestMessage?.body}
          </p>
        </div>
        <div className="mt-5 rounded-2xl border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2 text-sm">
            <AriIcon variant="gradient" className="size-4 shrink-0" aria-hidden="true" />
            <TextShimmer as="span" duration={2} className="font-medium">
              AI Summary
            </TextShimmer>
          </div>
          <p className="text-pretty text-sm leading-7 text-foreground">{thread.aiSummary}</p>
        </div>
      </section>
      <section>
        <div className="mb-3 text-xs font-semibold text-muted-foreground">Tracker</div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <InfoRow label="Current Stage" value={thread.pipeline.stage} />
          <InfoRow label="Last Updated" value={thread.date} />
        </div>
        <div className="mt-5 rounded-2xl border border-border bg-muted/30 p-4">
          <div className="mb-2 text-xs font-semibold text-muted-foreground">Next Step</div>
          <p className="text-pretty text-sm leading-6 text-foreground">{nextAction}</p>
        </div>
      </section>
    </div>
  );
}

/** Timeline entry as the deal-room renders it. Both the real
 *  (IntermediateActivityEvent) and the thread-derived fallback collapse
 *  into this shape so the render body is shared. */
interface DealRoomTimelineEntry {
  title: string;
  body: string;
  date: string;
}

function threadToTimelineEntries(thread: AriInboxThread): DealRoomTimelineEntry[] {
  // Unchanged from the original demo-only fallback — keep this byte-identical
  // so /demo/* renders exactly as before for unlinked threads.
  return [
    { title: "Deal created", body: "Added to the tracker", date: "Apr 20" },
    { title: "Initial email received", body: thread.from.name, date: thread.date },
    { title: "Terms reviewed", body: "Open items and next step identified", date: thread.date },
    { title: `Moved to ${thread.pipeline.stage}`, body: "Current tracker stage", date: "Apr 27" },
  ];
}

function activityEventsToTimelineEntries(
  events: IntermediateActivityEvent[],
): DealRoomTimelineEntry[] {
  return events.map((evt) => {
    let when = "";
    try {
      when = new Date(evt.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      when = "";
    }
    return {
      title: evt.actor ? `${evt.actor} — ${evt.eventType}` : evt.eventType,
      body: evt.summary,
      date: when,
    };
  });
}

function DealRoomTimeline(
  props: { events: IntermediateActivityEvent[] } | { thread: AriInboxThread },
) {
  // Real activity events (deal-linked) vs. thread-derived fallback. Empty
  // real data renders an honest empty state rather than fabricating events.
  const isReal = "events" in props;
  const entries = isReal
    ? activityEventsToTimelineEntries(props.events)
    : threadToTimelineEntries(props.thread);

  if (isReal && entries.length === 0) {
    return (
      <div>
        <div className="mb-4 text-xs font-semibold text-muted-foreground">Activity Timeline</div>
        <p className="px-1 text-sm text-muted-foreground">No activity yet</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 text-xs font-semibold text-muted-foreground">Activity Timeline</div>
      <div className="space-y-4">
        {entries.map((event, index) => (
          <div key={`${event.title}-${index}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={cn("mt-1 size-2 rounded-full", index === entries.length - 1 ? "bg-primary" : "bg-muted")} />
              {index < entries.length - 1 && <div className="mt-2 h-full w-px bg-muted" />}
            </div>
            <div className="min-w-0 flex-1 pb-3">
              <div className="text-sm font-medium text-foreground">{event.title}</div>
              <div className="text-sm text-muted-foreground">{event.body}</div>
            </div>
            <div className="text-xs text-muted-foreground/70">{event.date}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** (d) Persisted deal-room note. Hydrates from listDealThreadsApi(dealId)
 *  (the dealThread.notes column already exists — no schema change) and
 *  persists on blur via updateDealThreadApi. Picks the first dealThread for
 *  the deal as the note carrier, matching the single-note affordance the
 *  bare Textarea implied. */
function DealRoomNotes({ dealId }: { dealId: string }) {
  const { data, isLoading } = useSWR(["deal-room-threads", dealId], () =>
    listDealThreadsApi(dealId),
  );
  const dealThread = useMemo(() => {
    const map = data?.dealThreads ?? {};
    return Object.values(map)[0] ?? null;
  }, [data]);

  const [note, setNote] = useState<string | null>(null);
  // Hydrate the editable note from the persisted value once it loads.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (dealThread && hydratedFor.current !== dealThread.dealThreadId) {
      setNote(dealThread.notes ?? "");
      hydratedFor.current = dealThread.dealThreadId;
    }
  }, [dealThread]);

  const handleBlur = useCallback(async () => {
    if (!dealThread || note === null) return;
    if (note === (dealThread.notes ?? "")) return;
    try {
      await updateDealThreadApi(dealId, dealThread.dealThreadId, { notes: note });
      dealThread.notes = note;
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    }
  }, [dealId, dealThread, note]);

  if (isLoading) {
    return (
      <p className="px-1 py-2 text-sm text-muted-foreground">Loading notes…</p>
    );
  }
  if (!dealThread) {
    // No dealThread row to persist to (e.g. an email-sourced deal whose thread
    // lives only in deal_thread_links). Show a read-only empty state rather than
    // an editable field whose contents would be silently dropped on blur.
    return (
      <p className="px-1 py-2 text-sm text-muted-foreground">
        Notes will be available once this deal has a linked thread.
      </p>
    );
  }

  return (
    <Textarea
      aria-label="Deal notes"
      placeholder="Add notes about this deal..."
      className="bg-card"
      value={note ?? ""}
      onChange={(event) => setNote(event.target.value)}
      onBlur={handleBlur}
    />
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground/70">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

function ComposeDialog({
  open,
  value,
  onOpenChange,
  onChange,
  onSend,
}: {
  open: boolean;
  value: ComposeState;
  onOpenChange: (open: boolean) => void;
  onChange: (value: ComposeState) => void;
  onSend: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Popup className="max-w-2xl bg-background p-0 text-foreground" showCloseButton={false}>
        <Dialog.Header className="border-b border-border px-5 py-4">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base">New Message</Dialog.Title>
            <Dialog.Close
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Close compose dialog"
            >
              <X className="size-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Compose a simulated email for the Ari demo inbox.
          </Dialog.Description>
        </Dialog.Header>
        <div className="space-y-0">
          <div className="border-b border-border px-5 py-3">
            <Input
              aria-label="Recipient"
              value={value.to}
              onChange={(event) => onChange({ ...value, to: event.target.value })}
              placeholder="recipient@company.com"
              unstyled
              className="text-sm text-foreground"
            />
          </div>
          <div className="border-b border-border px-5 py-3">
            <Input
              aria-label="Subject"
              value={value.subject}
              onChange={(event) => onChange({ ...value, subject: event.target.value })}
              placeholder="Subject"
              unstyled
              className="text-sm text-foreground"
            />
          </div>
          <Textarea
            aria-label="Message body"
            value={value.body}
            onChange={(event) => onChange({ ...value, body: event.target.value })}
            placeholder="Compose your message..."
            className="rounded-none border-0 bg-background shadow-none"
            rows={9}
          />
        </div>
        <Dialog.Footer className="border-t border-border bg-card px-5 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Discard
          </Button>
          <Button onClick={onSend}>
            <Send size={16} aria-hidden="true" />
            Send
          </Button>
        </Dialog.Footer>
      </Dialog.Popup>
    </Dialog>
  );
}

function AriTag({ id }: { id: string }) {
  const tag = ARI_LABELS[id];
  // Mock demo-tag palette (music-deal/brand-deal/...) OR the engine-classified
  // contract-type palette (recording/producer/services/...). The engine types
  // arrive lowercase from the api-server projection; match case-insensitively
  // so a chip carrying "Recording" or "recording" both color correctly. Without
  // the engine map these fell through to the gray CLIENT_TAG_TONE.
  const dealLabel = isDealTypeTag(id) ? ARI_DEAL_LABEL_COLORS[id] : null;
  const engineLabel = ENGINE_DEAL_TYPE_COLORS[id.toLowerCase()] ?? null;
  const resolved = dealLabel ?? engineLabel;
  const label = tag?.name ?? resolved?.name ?? tagLabel(id);
  return (
    <Badge
      variant="outline"
      size="sm"
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-semibold shadow-none",
        resolved?.className ?? CLIENT_TAG_TONE,
      )}
    >
      {label}
    </Badge>
  );
}

function EmptyPane({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex h-full min-h-64 flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 rounded-full bg-muted/50 p-4 text-muted-foreground/70">{icon}</div>
      <h3 className="mb-1 font-medium text-foreground">{title}</h3>
      <p className="max-w-xs text-pretty text-sm text-muted-foreground/70">{body}</p>
    </div>
  );
}

function alertClass(urgency: AriInsightAlert["urgency"]) {
  switch (urgency) {
    case "high":
      return "border-border bg-muted/40";
    case "medium":
      return "border-border bg-muted/40";
    case "low":
      return "border-border bg-muted/40";
  }
}

function alertDotClass(urgency: AriInsightAlert["urgency"]) {
  switch (urgency) {
    case "high":
      return "bg-destructive";
    case "medium":
      return "bg-warning";
    case "low":
      return "bg-primary";
  }
}

function AttachmentTypeIcon({ type }: { type: string }) {
  const size = 18;
  switch (type.toLowerCase()) {
    case "pdf":
      return <FileText size={size} aria-hidden="true" />;
    default:
      return <FileText size={size} aria-hidden="true" />;
  }
}

function attachmentToneClass(tone: AriInboxAttachment["tone"]) {
  const tag = "border-0 font-semibold text-white shadow-none";
  switch (tone) {
    case "success":
      return cn(tag, "bg-success");
    case "warning":
      return cn(tag, "bg-warning");
    case "danger":
      return cn(tag, "bg-destructive");
    case "info":
      return cn(tag, "bg-primary");
    case "neutral":
      return cn(tag, "bg-muted-foreground");
  }
}

export default AriDemoInboxPage;